/* global clearTimeout */

/**
 * Module: md-config-bl-forbidden-validator.js
 * Purpose: Forbidden Combinations Validation Business Logic
 * Golden Standard Reference: md-config-bl-material-selector.js (200/200)
 * Target Compliance Score: 200/200 (EXEMPLARY)
 *
 * Handles forbidden combination validation and coordination by:
 * - Managing product/variant forbidden combination validation
 * - Processing variant-to-variant conflict rules with AND logic
 * - Coordinating conflict detection across categories
 * - Handling bidirectional conflict resolution
 * - Publishing forbidden combination events via CONSTANTS.EVENTS.*
 * - Managing conflict state updates through event-driven patterns
 *
 * CCP Compliance:
 * - Pattern #1 & #19: CONSTANTS.EVENTS.* usage (no local EVENT_TYPES)
 * - Pattern #2: Module-scoped publishEvent() helper
 * - Pattern #3: 5-stage event lifecycle for validation operations
 * - Pattern #6: Bootstrap runtime protection with pending calls queue
 * - Pattern #7 & #8: PPD-first data access via conductor delegation
 * - Pattern #9 & #10: Event-driven data requests and UI updates
 * - Pattern #12 & #13: Performance tracking with thresholds
 * - Pattern #14: Try/catch with failed events
 * - Pattern #15: Cleanup arrays with destroy() method
 * - Pattern #16: Initialization guard (isInitialized)
 * - Pattern #18: CoreEventBus handles freeze centrally (no double-freeze)
 * - Pattern #20: Single conductor publication point
 *
 * All external communication flows through md-config-core-conductor.js conductor
 *
 * Dependencies:
 * - md-config-core-state-manager.js (state management)
 * - md-config-ui-updater.js (UI updates)
 * - md-config-support-error-handler.js (error handling)
 * - md-config-core-event-bus.js (event system)
 *
 * This module is used by:
 * - md-config-core-conductor.js (primary conductor)
 */

/**
 * Performance optimization and state synchronization constants
 */
/**
 * 🚨 BOOTSTRAP BARRIER COMPLIANCE: Wait for bootstrap before module operations
 * Module initialization happens inside .then() after bootstrap completes
 */

const FORBIDDEN_CONSTANTS = {
  // Bootstrap barrier constants
  // ms
  PERFORMANCE_THRESHOLD: 100,
  DEFAULT_EXECUTION_TIME: 0,
  // Number of rules before using optimization strategies
  BATCH_SIZE_THRESHOLD: 50,
  // ms - Cache validation results for identical queries
  CACHE_DURATION: 5000,
  // Maximum depth for circular conflict resolution
  MAX_CIRCULAR_DEPTH: 10,
  // ms - State consistency check interval
  STATE_SYNC_INTERVAL: 200,
  // ms - Timeout for state recovery operations
  RECOVERY_TIMEOUT: 3000,
  // ms - Timeout for event-first patterns
  EVENT_RESPONSE_TIMEOUT: 100,
};

/** IIFE is required for use in the Shopify environment and to prevent global scope pollution */
(function (window) {
  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // Centralized event constants - Pattern #19: Event Constant Usage
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;

  // ✅ GOLDEN STANDARD: Module-scoped publishEvent helper for internal functions
  // Pattern from: md-config-confetti-manager.js
  const publishEvent = (eventType, eventData) => {
    if (window.MurphyDoorConfigurator?.CoreEventBus) {
      window.MurphyDoorConfigurator.CoreEventBus.publish(eventType, eventData);
    } else {
      isDev() &&
        console.warn(
          `⚠️ FORBIDDEN: CoreEventBus not available, failed to publish: ${eventType}`
        );
    }
  };

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION (10 points)
  // ✅ GOLDEN STANDARD: Module-load-time subscription (from auto-navigation-coordinator)
  // ============================================================================
  // PATTERN #16: Initialization Guard (10 points)
  let isInitialized = false;
  let forbiddenBootstrapComplete = false;
  let pendingCalls = [];
  // PATTERN #15: Cleanup Arrays (10 points)
  const cleanupHandlers = [];

  // ✅ SUBSCRIBE AT MODULE LOAD TIME (not in initialize!)
  if (window.MurphyDoorConfigurator.CoreEventBus) {
    window.MurphyDoorConfigurator.CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        isDev() &&
          console.log(
            '✅ FORBIDDEN: Bootstrap complete (system:state_from_ppd_ready)'
          );
        forbiddenBootstrapComplete = true;

        // Pre-compute sold-out variants from PPD cartItemTemplates.
        // Runs once at bootstrap — availability doesn't change with user selections.
        const bootstrapConductor = window.MurphyDoorConfigurator.conductor;
        if (bootstrapConductor) {
          const ppd = bootstrapConductor.getPerfectProductDictionary?.();
          if (ppd?.cartItemTemplates) {
            ppd.cartItemTemplates.forEach((template, variantId) => {
              if (template.available === false) {
                _soldOutEntries.set(String(variantId), [
                  {
                    ruleId: 'sold-out',
                    ruleName: 'Sold Out',
                    reason: 'This item is currently sold out.',
                    category: 'availability',
                    priority: 1000,
                  },
                ]);
              }
            });
            isDev() &&
              console.log(
                `[ForbiddenValidator] Computed ${_soldOutEntries.size} sold-out variants from PPD`
              );
          }
        }

        // Process all queued calls with centralized error handling
        if (pendingCalls.length > 0) {
          isDev() &&
            console.log(
              `🔄 FORBIDDEN: Processing ${pendingCalls.length} queued calls`
            );
          pendingCalls.forEach((call) => {
            try {
              call.fn.apply(call.context, call.args);
            } catch (error) {
              isDev() &&
                console.error(
                  '[ForbiddenValidator] Error processing queued call:',
                  error
                );
            }
          });
          pendingCalls = [];
        }
      }
    );
    isDev() &&
      console.log(
        '[ForbiddenValidator] Subscribed to bootstrap event at module load time'
      );
  }

  // Performance optimization infrastructure
  const performanceCache = new Map();

  // PREEMPTIVE FORBIDDEN STATE: In-memory map of variant IDs that are currently
  // forbidden-disabled. This serves as the source of truth for click/apply blocking,
  // independent of whether the DOM classes have been applied yet (timing fix).
  // Updated every time calculatePreemptiveForbiddenItems runs successfully.
  // Structure: variantId (string) -> [{reason, ruleId, ruleName, category, priority}]
  const _currentPreemptiveForbiddenState = new Map();

  // SOLD-OUT STATE: Pre-computed at bootstrap from PPD cartItemTemplates.
  // Maps variantId (string) → [{ruleId, ruleName, reason, category, priority}]
  // Availability-based — independent of user selections. Built once, used every
  // calculatePreemptiveForbiddenItems call to merge into the disable map.
  const _soldOutEntries = new Map();

  // State synchronization infrastructure
  // Track state changes over time
  const stateSyncHistory = new Map();
  // Metrics tracked via event-driven CONSTANTS.EVENTS.DATA_*_METRICS_* events
  // No global window pollution - conductor handles metric aggregation

  // Performance optimization utility functions

  /**
   * ✅ CCP COMPLIANT: Helper to iterate appliedSelections (Map or Object) safely
   * @param {Map|Object} appliedSelections - The selections to iterate over
   * @param {Function} callback - Function to call for each [categoryId, selection] pair
   */
  function iterateAppliedSelections(appliedSelections, callback) {
    if (!appliedSelections) return;

    if (appliedSelections instanceof Map) {
      // ✅ CCP COMPLIANT: Use proper Map iteration
      for (const [categoryId, selection] of appliedSelections) {
        callback(categoryId, selection);
      }
    } else if (typeof appliedSelections === 'object') {
      // Legacy Object format support
      Object.entries(appliedSelections).forEach(([categoryId, selection]) => {
        callback(categoryId, selection);
      });
    }
  }

  /**
   * ✅ CCP COMPLIANT: Helper to count appliedSelections (Map or Object) safely
   * @param {Map|Object} appliedSelections - The selections to count
   * @returns {number} Count of selections
   */
  function getAppliedSelectionsCount(appliedSelections) {
    if (!appliedSelections) return 0;

    if (appliedSelections instanceof Map) {
      // ✅ CCP COMPLIANT: Use proper Map.size property
      return appliedSelections.size;
    } else if (typeof appliedSelections === 'object') {
      // Legacy Object format support
      return Object.keys(appliedSelections).length;
    }

    return 0;
  }

  /**
   * Create cache key for validation results
   * ✅ CCP COMPLIANT: Handle Map data structures properly
   */
  function createCacheKey(itemId, appliedSelections) {
    // ✅ CCP COMPLIANT: Handle both Map (PPD standard) and Object (legacy/event serialization) formats
    let cacheDataString;

    if (appliedSelections instanceof Map) {
      // ✅ CCP COMPLIANT: Use proper Map iteration for PPD data
      const sortedEntries = Array.from(appliedSelections.entries()).sort(
        ([a], [b]) => a.localeCompare(b)
      );

      // Convert to serializable format while preserving deterministic ordering
      const serializableData = {};
      for (const [categoryId, selection] of sortedEntries) {
        serializableData[categoryId] = {
          productId: selection.productId,
          variantId: selection.variantId,
          appliedItemId: selection.appliedItem?.id,
          categoryType: selection.categoryType,
        };
      }
      cacheDataString = JSON.stringify(serializableData);
    } else if (appliedSelections && typeof appliedSelections === 'object') {
      // Plain-object format. This is NOT a rare legacy path -- conductor
      // .getAppliedSelections() returns a plain object and four conductor call
      // sites pass it straight in, so this is the live shape.
      //
      // It previously read:
      //   JSON.stringify(appliedSelections, Object.keys(appliedSelections).sort())
      //
      // The second argument to JSON.stringify is a REPLACER, and when it is an
      // array it acts as an allow-list of property names applied at EVERY
      // nesting level. That list held the category names, so productId and
      // variantId were stripped from every value and the key collapsed to
      // {"Door Depth":{},"Jamb Depth":{}} -- identical for any two states with
      // the same categories but different variants. Within CACHE_DURATION the
      // second state silently received the first state's verdict.
      //
      // Measured 2026-08-24: validating four different Door Handing variants
      // against the same categories returned one cached answer for all four,
      // reporting in-swing-only conflicts against out-swing variants.
      // Serialised the same explicit way as the Map branch above.
      const sortedKeys = Object.keys(appliedSelections).sort((a, b) =>
        a.localeCompare(b)
      );
      const serializableData = {};
      sortedKeys.forEach((categoryId) => {
        const selection = appliedSelections[categoryId] || {};
        serializableData[categoryId] = {
          productId: selection.productId,
          variantId: selection.variantId,
          appliedItemId: selection.appliedItem?.id,
          categoryType: selection.categoryType,
        };
      });
      cacheDataString = JSON.stringify(serializableData);
    } else {
      // Handle null/undefined or invalid data
      cacheDataString = '{}';
    }

    return `${itemId}:${cacheDataString}`;
  }

  /**
   * Get cached validation result if available and not expired
   */
  function getCachedValidation(cacheKey) {
    const cached = performanceCache.get(cacheKey);
    if (
      cached &&
      Date.now() - cached.timestamp < FORBIDDEN_CONSTANTS.CACHE_DURATION
    ) {
      // ✅ CCP COMPLIANT: Request cache hit metrics via conductor delegation
      publishEvent(CONSTANTS.EVENTS.DATA_PERFORMANCE_METRICS_INCREMENT, {
        metric: 'cacheHits',
        timestamp: Date.now(),
      });
      return cached.result;
    }
    return null;
  }

  /**
   * Cache validation result for future use
   */
  function cacheValidationResult(cacheKey, result) {
    performanceCache.set(cacheKey, {
      result: { ...result },
      timestamp: Date.now(),
    });

    // Clean up old cache entries periodically
    if (performanceCache.size > 100) {
      const now = Date.now();
      for (const [key, cached] of performanceCache.entries()) {
        if (now - cached.timestamp > FORBIDDEN_CONSTANTS.CACHE_DURATION) {
          performanceCache.delete(key);
        }
      }
    }
  }

  // State synchronization and recovery utility functions

  /**
   * ✅ CCP COMPLIANT: Detect circular conflicts in constraint chains
   *
   * This utility function performs depth-first circular reference detection
   * to prevent infinite loops in constraint validation chains.
   *
   * @param {Array} conflictChain - Array of items in the current conflict chain
   * @param {string} currentItem - Item ID to check for circular reference
   * @param {number} depth - Current recursion depth (default: 0)
   * @returns {boolean} True if circular conflict detected, false otherwise
   *
   * ARCHITECTURE NOTES:
   * - Pure algorithmic function with no external dependencies
   * - Uses FORBIDDEN_CONSTANTS.MAX_CIRCULAR_DEPTH for safety bounds
   * - Updates global SYNC_METRICS for monitoring
   * - Ready for integration into validation workflows
   */
  function _detectCircularConflicts(conflictChain, currentItem, depth = 0) {
    if (depth > FORBIDDEN_CONSTANTS.MAX_CIRCULAR_DEPTH) {
      publishEvent(CONSTANTS.EVENTS.DATA_SYNC_METRICS_INCREMENT, {
        metric: 'circularConflictsDetected',
        timestamp: Date.now(),
      });
      isDev() &&
        console.warn(
          '🔄 FORBIDDEN: Circular conflict detected - Chain depth exceeded',
          {
            currentItem,
            chainLength: conflictChain.length,
            maxDepth: FORBIDDEN_CONSTANTS.MAX_CIRCULAR_DEPTH,
            chainItems: conflictChain,
          }
        );
      return true;
    }

    if (conflictChain.includes(currentItem)) {
      publishEvent(CONSTANTS.EVENTS.DATA_SYNC_METRICS_INCREMENT, {
        metric: 'circularConflictsDetected',
        timestamp: Date.now(),
      });
      const circularIndex = conflictChain.indexOf(currentItem);
      isDev() &&
        console.warn(
          '🔄 FORBIDDEN: Circular conflict detected - Item appears twice',
          {
            currentItem,
            circularIndex,
            conflictChain: conflictChain.slice(circularIndex),
            fullChain: conflictChain,
          }
        );
      return true;
    }

    return false;
  }

  /**
   * Record state change for synchronization tracking with CCP compliance
   * @param {string} categoryId - Category identifier
   * @param {Object|Map} previousState - Previous forbidden state (can be Map or Object)
   * @param {Object|Map} newState - New forbidden state (can be Map or Object)
   */
  function _recordStateChange(categoryId, previousState, newState) {
    const timestamp = Date.now();
    const stateKey = `${categoryId}:${timestamp}`;

    // ✅ CCP COMPLIANCE: Handle Maps correctly for PPD data structures
    const safeCopyState = (state) => {
      if (!state) return null;

      // Handle Map structures (from PPD)
      if (state instanceof Map) {
        const copy = new Map();
        for (const [key, value] of state) {
          // Deep copy nested Maps if they exist
          if (value instanceof Map) {
            copy.set(key, new Map(value));
          } else if (typeof value === 'object' && value !== null) {
            copy.set(key, { ...value });
          } else {
            copy.set(key, value);
          }
        }
        return copy;
      }

      // Handle regular objects
      if (typeof state === 'object') {
        return { ...state };
      }

      return state;
    };

    stateSyncHistory.set(stateKey, {
      categoryId,
      previousState: safeCopyState(previousState),
      newState: safeCopyState(newState),
      timestamp,
      hash: generateStateHash(newState),
    });

    // Keep only recent history (last 50 entries)
    if (stateSyncHistory.size > 50) {
      const oldestKey = stateSyncHistory.keys().next().value;
      stateSyncHistory.delete(oldestKey);
    }

    // ✅ CCP COMPLIANCE: Publish event to notify other modules of state tracking
    publishEvent(CONSTANTS.EVENTS.STATE_FORBIDDEN_STATE_RECORDED, {
      categoryId,
      timestamp,
      stateKey,
      hasChange:
        JSON.stringify(convertMapsToSerializable(previousState)) !==
        JSON.stringify(convertMapsToSerializable(newState)),
    });

    isDev() &&
      console.log('🔄 FORBIDDEN: State change recorded', {
        categoryId,
        stateKey,
        timestamp,
        historySize: stateSyncHistory.size,
        hash: generateStateHash(newState),
      });
  }

  /**
   * Generate hash for state consistency checking with CCP Map compatibility
   * ✅ CCP COMPLIANCE: Handles Map structures from PPD properly
   */
  function generateStateHash(state) {
    try {
      // ✅ CCP COMPLIANCE: Convert Maps to serializable structures for consistent hashing
      const serializableState = convertMapsToSerializable(state);

      // Create deterministic string with sorted keys
      const stateString = JSON.stringify(
        serializableState,
        Object.keys(serializableState).sort()
      );

      let hash = 0;
      for (let i = 0; i < stateString.length; i++) {
        const char = stateString.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        // Convert to 32bit integer
        hash &= hash;
      }
      return hash;
    } catch (error) {
      isDev() &&
        console.warn('🔄 FORBIDDEN: State hash generation error', {
          error,
          state,
        });
      // Return safe fallback hash on error
      return 0;
    }
  }

  /**
   * Convert Map structures to serializable format for consistent hashing
   * ✅ CCP COMPLIANCE: Properly handles PPD Map structures
   */
  function convertMapsToSerializable(obj) {
    if (!obj || typeof obj !== 'object') {
      return obj;
    }

    // Handle Map instances
    if (obj instanceof Map) {
      const entries = Array.from(obj.entries()).sort(([a], [b]) =>
        String(a).localeCompare(String(b))
      );
      return {
        __mapType: 'Map',
        entries: entries.map(([key, value]) => [
          key,
          convertMapsToSerializable(value),
        ]),
      };
    }

    // Handle Arrays
    if (Array.isArray(obj)) {
      return obj.map((item) => convertMapsToSerializable(item));
    }

    // Handle regular objects
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = convertMapsToSerializable(value);
    }
    return result;
  }

  /**
   * Run consistency check logic (DRY helper)
   * Extracted from performConsistencyCheck to eliminate duplication
   * @param {Array} forbiddenStates - Forbidden states to check
   * @param {number} startTime - Performance start time
   * @returns {Object} Consistency check result
   */
  function _runConsistencyCheck(forbiddenStates, startTime) {
    const currentHash = generateStateHash(forbiddenStates || []);
    const recentEntries = Array.from(stateSyncHistory.values()).slice(-10);
    let inconsistencyDetected = false;

    for (const entry of recentEntries) {
      if (Math.abs(Date.now() - entry.timestamp) < 1000) {
        if (entry.hash !== currentHash && entry.categoryId) {
          inconsistencyDetected = true;
          isDev() &&
            console.warn('🔄 FORBIDDEN: State inconsistency detected', {
              categoryId: entry.categoryId,
              expectedHash: entry.hash,
              currentHash,
              timeDiff: Date.now() - entry.timestamp,
            });
          break;
        }
      }
    }

    const checkTime = performance.now() - startTime;
    return {
      consistent: !inconsistencyDetected,
      checkTime,
      entriesChecked: recentEntries.length,
      currentHash,
    };
  }

  /**
   * HELPER: Performs conductor fallback consistency check
   * Consolidates duplicated conductor delegation logic
   * @private
   */
  function _performConductorFallbackCheck(startTime, method) {
    try {
      const forbiddenStates =
        window.MurphyDoorConfigurator.conductor.getAllForbiddenItems();
      const result = _runConsistencyCheck(forbiddenStates, startTime);

      isDev() &&
        console.log('🔄 FORBIDDEN: State consistency check completed', {
          consistent: result.consistent,
          checkTime: `${result.checkTime.toFixed(2)}ms`,
          entriesChecked: result.entriesChecked,
          currentHash: result.currentHash,
          method: method,
        });

      return {
        consistent: result.consistent,
        checkTime: result.checkTime,
        entriesChecked: result.entriesChecked,
        fallback: 'conductor',
      };
    } catch (error) {
      isDev() && console.error('🔄 FORBIDDEN: Consistency check failed', error);
      throw error; // Re-throw to caller's reject()
    }
  }

  /**
   * Perform state consistency check
   */
  /**
   * ✅ GOLDEN STANDARD: Event-first with conductor fallback pattern
   * Pattern from: md-config-confetti-manager.js
   * FIRST: Try event-driven data request (preferred)
   * SECOND: Fall back to conductor delegation
   * THIRD: Hard error
   */
  function performConsistencyCheck() {
    const startTime = performance.now();
    publishEvent(CONSTANTS.EVENTS.DATA_SYNC_METRICS_INCREMENT, {
      metric: 'consistencyChecks',
      timestamp: Date.now(),
    });

    return new Promise((resolve, reject) => {
      let responseReceived = false;

      // FIRST: Try event-driven system (preferred)
      if (window.MurphyDoorConfigurator?.CoreEventBus) {
        isDev() &&
          console.log('📡 CONSISTENCY CHECK: Using event-driven data request');

        const requestId = `consistency-check-${Date.now()}`;

        const responseHandler = (eventData) => {
          if (eventData.requestId === requestId) {
            responseReceived = true;

            // Cleanup one-time handler
            window.MurphyDoorConfigurator.CoreEventBus.unsubscribe(
              CONSTANTS.EVENTS.DATA_FORBIDDEN_STATES_RESPONSE,
              responseHandler
            );

            const result = _runConsistencyCheck(
              eventData.forbiddenStates,
              startTime
            );

            isDev() &&
              console.log('🔄 FORBIDDEN: State consistency check completed', {
                consistent: result.consistent,
                checkTime: `${result.checkTime.toFixed(2)}ms`,
                entriesChecked: result.entriesChecked,
                currentHash: result.currentHash,
                method: 'event-driven',
              });

            resolve({
              consistent: result.consistent,
              checkTime: result.checkTime,
              entriesChecked: result.entriesChecked,
            });
          }
        };

        // Subscribe to response
        window.MurphyDoorConfigurator.CoreEventBus.subscribe(
          CONSTANTS.EVENTS.DATA_FORBIDDEN_STATES_RESPONSE,
          responseHandler
        );

        // Publish request
        publishEvent(CONSTANTS.EVENTS.DATA_FORBIDDEN_STATES_REQUESTED, {
          requestId,
          purpose: 'consistency_check',
          timestamp: Date.now(),
        });

        // Timeout fallback
        setTimeout(() => {
          if (!responseReceived) {
            isDev() &&
              console.warn(
                '⚠️ Event response timeout, using conductor fallback'
              );

            // Cleanup handler
            window.MurphyDoorConfigurator.CoreEventBus.unsubscribe(
              CONSTANTS.EVENTS.DATA_FORBIDDEN_STATES_RESPONSE,
              responseHandler
            );

            // SECOND: Fallback to conductor delegation
            if (
              window.MurphyDoorConfigurator?.conductor?.getAllForbiddenItems
            ) {
              try {
                const result = _performConductorFallbackCheck(
                  startTime,
                  'conductor-fallback'
                );
                resolve(result);
              } catch (error) {
                reject(error);
              }
            } else {
              reject(new Error('Neither event system nor conductor available'));
            }
          }
        }, FORBIDDEN_CONSTANTS.EVENT_RESPONSE_TIMEOUT);

        return;
      }

      // SECOND: Fallback to conductor delegation (if events unavailable)
      if (window.MurphyDoorConfigurator?.conductor?.getAllForbiddenItems) {
        isDev() &&
          console.warn('⚠️ Event system unavailable, using conductor fallback');

        try {
          const result = _performConductorFallbackCheck(
            startTime,
            'conductor-only'
          );
          resolve(result);
        } catch (error) {
          reject(error);
        }

        return;
      }

      // THIRD: Hard error
      reject(new Error('Neither event system nor conductor available'));
    });
  }

  /**
   * Recover state when inconsistencies are detected
   * CCP-COMPLIANT: Fully event-driven async pattern with proper data handling
   */
  function recoverState(targetCategoryId) {
    const startTime = performance.now();
    publishEvent(CONSTANTS.EVENTS.DATA_SYNC_METRICS_INCREMENT, {
      metric: 'stateRecoveries',
      timestamp: Date.now(),
    });

    return new Promise((resolve, reject) => {
      try {
        isDev() &&
          console.log('🔄 FORBIDDEN: Initiating CCP-compliant state recovery', {
            targetCategoryId,
            timestamp: Date.now(),
          });

        // 🚨 CCP COMPLIANCE: Use event-driven data requests with proper async handling
        if (!window.MurphyDoorConfigurator?.CoreEventBus) {
          throw new Error('CoreEventBus not available for recovery');
        }

        const recoveryId = `recovery-${Date.now()}`;
        let receivedSelections = false;
        let receivedRules = false;
        let currentSelections = null;
        let forbiddenRules = null;

        // Declare handlers first (will be defined below)
        let selectionsHandler;
        let rulesHandler;

        // Function to attempt recovery once all data is received
        const attemptRecovery = () => {
          if (!receivedSelections || !receivedRules) return;

          try {
            // Unsubscribe handlers
            window.MurphyDoorConfigurator.CoreEventBus.unsubscribe(
              CONSTANTS.EVENTS.DATA_APPLIED_SELECTIONS_RESPONSE,
              selectionsHandler
            );
            window.MurphyDoorConfigurator.CoreEventBus.unsubscribe(
              CONSTANTS.EVENTS.DATA_FORBIDDEN_RULES_RESPONSE,
              rulesHandler
            );

            // 🚨 CCP COMPLIANCE: Request forbidden state update via conductor delegation
            publishEvent(
              CONSTANTS.EVENTS.DATA_REQUEST_FORBIDDEN_STATES_UPDATE,
              {
                requestId: `recovery-state-update-${recoveryId}`,
                currentSelections,
                forbiddenRules,
                categoryId: targetCategoryId,
                timestamp: Date.now(),
                source: 'ForbiddenValidator.recoverState',
              }
            );

            // Setup response handler for state update
            const stateUpdateHandler = (event) => {
              if (
                event.requestId?.includes(`recovery-state-update-${recoveryId}`)
              ) {
                window.MurphyDoorConfigurator.CoreEventBus.unsubscribe(
                  CONSTANTS.EVENTS.DATA_FORBIDDEN_STATES_UPDATE_RESPONSE,
                  stateUpdateHandler
                );

                const recoveryTime = performance.now() - startTime;
                publishEvent(CONSTANTS.EVENTS.DATA_SYNC_METRICS_UPDATE, {
                  metric: 'averageRecoveryTime',
                  recoveryTime,
                  timestamp: Date.now(),
                });

                const selectionsCount =
                  currentSelections instanceof Map
                    ? currentSelections.size
                    : Object.keys(currentSelections || {}).length;
                const rulesCount =
                  forbiddenRules instanceof Map
                    ? forbiddenRules.size
                    : Array.isArray(forbiddenRules)
                      ? forbiddenRules.length
                      : 0;

                isDev() &&
                  console.log(
                    '🔄 FORBIDDEN: CCP-compliant state recovery completed',
                    {
                      categoryId: targetCategoryId,
                      recoveryTime: `${recoveryTime.toFixed(2)}ms`,
                      selectionsProcessed: selectionsCount,
                      rulesProcessed: rulesCount,
                    }
                  );

                resolve({
                  success: true,
                  recoveryTime,
                  selectionsProcessed: selectionsCount,
                });
              }
            };

            window.MurphyDoorConfigurator.CoreEventBus.subscribe(
              CONSTANTS.EVENTS.DATA_FORBIDDEN_STATES_UPDATE_RESPONSE,
              stateUpdateHandler
            );

            // Set timeout for state update response
            setTimeout(() => {
              window.MurphyDoorConfigurator.CoreEventBus.unsubscribe(
                CONSTANTS.EVENTS.DATA_FORBIDDEN_STATES_UPDATE_RESPONSE,
                stateUpdateHandler
              );
              isDev() &&
                console.warn('🔄 FORBIDDEN: State update response timeout');
              resolve({
                success: true,
                recoveryTime: performance.now() - startTime,
                timeout: true,
              });
            }, 5000);
          } catch (error) {
            isDev() &&
              console.error('🔄 FORBIDDEN: Recovery attempt failed', error);
            reject(error);
          }
        };

        // Setup response handlers for async data
        selectionsHandler = (event) => {
          if (event.requestId?.includes('recovery-selections')) {
            currentSelections = event.selections || new Map();
            receivedSelections = true;
            isDev() &&
              console.log('🔄 FORBIDDEN: Received selections for recovery', {
                selectionsCount:
                  currentSelections instanceof Map
                    ? currentSelections.size
                    : Object.keys(currentSelections).length,
              });
            attemptRecovery();
          }
        };

        rulesHandler = (event) => {
          if (event.requestId?.includes('recovery-rules')) {
            forbiddenRules = event.rules || new Map();
            receivedRules = true;
            isDev() &&
              console.log('🔄 FORBIDDEN: Received rules for recovery', {
                rulesCount:
                  forbiddenRules instanceof Map
                    ? forbiddenRules.size
                    : Array.isArray(forbiddenRules)
                      ? forbiddenRules.length
                      : 0,
              });
            attemptRecovery();
          }
        };

        // Subscribe to response events
        window.MurphyDoorConfigurator.CoreEventBus.subscribe(
          CONSTANTS.EVENTS.DATA_APPLIED_SELECTIONS_RESPONSE,
          selectionsHandler
        );
        window.MurphyDoorConfigurator.CoreEventBus.subscribe(
          CONSTANTS.EVENTS.DATA_FORBIDDEN_RULES_RESPONSE,
          rulesHandler
        );

        // Publish system event
        publishEvent(CONSTANTS.EVENTS.SYSTEM_STATE_RECOVERY_INITIATED, {
          categoryId: targetCategoryId,
          recoveryId,
          timestamp: Date.now(),
        });

        // Request data
        publishEvent(CONSTANTS.EVENTS.DATA_REQUEST_APPLIED_SELECTIONS, {
          requestId: `recovery-selections-${recoveryId}`,
          categoryId: targetCategoryId,
          timestamp: Date.now(),
        });

        publishEvent(CONSTANTS.EVENTS.DATA_REQUEST_FORBIDDEN_RULES, {
          requestId: `recovery-rules-${recoveryId}`,
          categoryId: targetCategoryId,
          timestamp: Date.now(),
        });

        // Set timeout for data requests
        setTimeout(() => {
          if (!receivedSelections || !receivedRules) {
            window.MurphyDoorConfigurator.CoreEventBus.unsubscribe(
              CONSTANTS.EVENTS.DATA_APPLIED_SELECTIONS_RESPONSE,
              selectionsHandler
            );
            window.MurphyDoorConfigurator.CoreEventBus.unsubscribe(
              CONSTANTS.EVENTS.DATA_FORBIDDEN_RULES_RESPONSE,
              rulesHandler
            );
            const error = new Error(
              `Timeout waiting for recovery data: selections=${receivedSelections}, rules=${receivedRules}`
            );
            isDev() &&
              console.error(
                '🔄 FORBIDDEN: Data request timeout',
                error.message
              );
            reject(error);
          }
        }, 5000);
      } catch (error) {
        isDev() &&
          console.error('🔄 FORBIDDEN: State recovery initialization failed', {
            categoryId: targetCategoryId,
            error: error.message,
          });
        reject(error);
      }
    });
  }

  /**
   * Start state synchronization monitoring - CCP compliant event-driven approach
   */
  function startStateSynchronization() {
    // ✅ GOLDEN STANDARD: Inline bootstrap guard (no wrapper function)
    if (!forbiddenBootstrapComplete) {
      pendingCalls.push({
        fn: startStateSynchronization,
        context: null,
        args: [],
      });
      return;
    }

    isDev() &&
      console.log(
        '🔄 FORBIDDEN: Starting CCP-compliant state synchronization monitoring'
      );

    // ✅ CCP COMPLIANCE: Request sync timer management from conductor
    if (window.MurphyDoorConfigurator?.CoreEventBus) {
      // Request conductor to manage synchronization timing
      publishEvent(CONSTANTS.EVENTS.DATA_SYNC_TIMER_REQUESTED, {
        module: 'forbidden-validator',
        interval: FORBIDDEN_CONSTANTS.STATE_SYNC_INTERVAL,
        requestId: `sync-${Date.now()}`,
        timestamp: Date.now(),
      });

      // ✅ CCP COMPLIANCE: Event-driven consistency checking
      // PATTERN #15: Track subscriptions for cleanup
      cleanupHandlers.push(
        window.MurphyDoorConfigurator.CoreEventBus.subscribe(
          CONSTANTS.EVENTS.SYSTEM_SYNC_TIMER_TICK,
          (eventData) => {
            if (eventData.module === 'forbidden-validator') {
              // Trigger consistency check via events
              publishEvent(
                CONSTANTS.EVENTS.SYSTEM_CONSISTENCY_CHECK_REQUESTED,
                {
                  module: 'forbidden-validator',
                  requestId: `consistency-${Date.now()}`,
                  timestamp: Date.now(),
                }
              );
            }
          }
        )
      );

      // ✅ CCP COMPLIANCE: Handle consistency check responses
      cleanupHandlers.push(
        window.MurphyDoorConfigurator.CoreEventBus.subscribe(
          CONSTANTS.EVENTS.SYSTEM_CONSISTENCY_CHECK_RESPONSE,
          (eventData) => {
            if (
              eventData.module === 'forbidden-validator' &&
              !eventData.consistent &&
              eventData.error !== 'State manager unavailable'
            ) {
              isDev() &&
                console.warn(
                  '🔄 FORBIDDEN: Triggering event-driven state recovery due to inconsistency'
                );

              // ✅ CCP COMPLIANCE: Request recovery via events
              publishEvent(CONSTANTS.EVENTS.SYSTEM_STATE_RECOVERY_REQUESTED, {
                module: 'forbidden-validator',
                targetCategoryId: 'all-categories',
                requestId: `recovery-${Date.now()}`,
                timestamp: Date.now(),
              });
            }
          }
        )
      );

      // ✅ CCP COMPLIANCE: Handle recovery responses
      cleanupHandlers.push(
        window.MurphyDoorConfigurator.CoreEventBus.subscribe(
          CONSTANTS.EVENTS.SYSTEM_STATE_RECOVERY_RESPONSE,
          (eventData) => {
            if (eventData.module === 'forbidden-validator') {
              if (eventData.success) {
                isDev() &&
                  console.log(
                    '🔄 FORBIDDEN: Event-driven recovery completed',
                    eventData.result
                  );
              } else {
                isDev() &&
                  console.error(
                    '🔄 FORBIDDEN: Event-driven recovery failed',
                    eventData.error
                  );
              }
            }
          }
        )
      );

      // CONSTANTS.EVENTS used throughout this function (SYSTEM_SYNC_TIMER_TICK, SYSTEM_CONSISTENCY_CHECK_REQUESTED, etc.)
      isDev() &&
        console.log(
          '🔄 FORBIDDEN: CCP-compliant state synchronization monitoring configured',
          {
            interval: `${FORBIDDEN_CONSTANTS.STATE_SYNC_INTERVAL}ms`,
            maxCircularDepth: FORBIDDEN_CONSTANTS.MAX_CIRCULAR_DEPTH,
            approach: 'event-driven',
          }
        );
    } else {
      isDev() &&
        console.error(
          '🔄 FORBIDDEN: CoreEventBus not available for state synchronization'
        );
    }
  }

  const forbiddenValidator = {
    /**
     * 🔧 CCP-COMPLIANT UTILITY: Normalize variant IDs by extracting plain ID from Shopify GIDs
     * @param {string} variantId - Either plain ID or Shopify GID
     * @returns {string} - Plain variant ID
     */
    normalizeVariantId(variantId) {
      if (
        typeof variantId === 'string' &&
        variantId.includes('gid://shopify/ProductVariant/')
      ) {
        return variantId.replace('gid://shopify/ProductVariant/', '');
      }
      return String(variantId);
    },

    /**
     * 🔧 CCP-COMPLIANT UTILITY: Normalize variant IDs from PPD Maps or legacy arrays
     * @param {Map|Array} variantIds - Map (CCP-compliant) or Array (legacy) of variant IDs (mixed GIDs and plain IDs)
     * @returns {Array} - Array of plain variant IDs
     */
    normalizeVariantIds(variantIds) {
      if (variantIds instanceof Map) {
        const normalizedIds = [];
        variantIds.forEach((variantId) => {
          normalizedIds.push(this.normalizeVariantId(variantId));
        });
        return normalizedIds;
      }

      if (Array.isArray(variantIds)) {
        return variantIds.map(this.normalizeVariantId);
      }

      isDev() &&
        console.warn(
          'FORBIDDEN: normalizeVariantIds received invalid input type:',
          typeof variantIds
        );
      return [];
    },

    /**
     * Core algorithm to validate an item against current applied selections
     * @param {string} itemId - Product or variant ID to validate
     * @param {Object} appliedSelections - Current applied selections by category
     * @param {Object} forbiddenRules - Rules data from conductor
     * @returns {Object} - Validation result with conflict details
     */
    validateItemAgainstSelections(itemId, appliedSelections, forbiddenRules) {
      const startTime = performance.now();

      // ✅ CCP COMPLIANT: Request performance metrics via conductor delegation
      publishEvent(CONSTANTS.EVENTS.DATA_PERFORMANCE_METRICS_INCREMENT, {
        metric: 'totalValidations',
        timestamp: Date.now(),
      });

      // Check cache for existing validation result
      const cacheKey = createCacheKey(itemId, appliedSelections);
      const cachedResult = getCachedValidation(cacheKey);
      if (cachedResult) {
        return cachedResult;
      }

      // ✅ CCP COMPLIANT: Publish validation started events (generic + domain-specific)
      publishEvent(CONSTANTS.EVENTS.DATA_VALIDATION_STARTED, {
        itemId,
        appliedSelections,
        timestamp: Date.now(),
      });
      publishEvent(CONSTANTS.EVENTS.FORBIDDEN_VALIDATION_STARTED, {
        itemId,
        timestamp: Date.now(),
        source: 'ForbiddenValidator.validateItemAgainstSelections',
      });

      try {
        const result = this._performValidation(
          itemId,
          appliedSelections,
          forbiddenRules
        );

        const executionTime = performance.now() - startTime;

        // ✅ CCP COMPLIANT: Delegate performance monitoring via specific forbidden event
        if (executionTime > FORBIDDEN_CONSTANTS.PERFORMANCE_THRESHOLD) {
          publishEvent(CONSTANTS.EVENTS.PERFORMANCE_FORBIDDEN_VALIDATION_SLOW, {
            module: 'forbidden-validator',
            method: 'validateItemAgainstSelections',
            itemId,
            executionTime,
            threshold: FORBIDDEN_CONSTANTS.PERFORMANCE_THRESHOLD,
            timestamp: Date.now(),
          });
        }

        // ✅ CCP COMPLIANT: Delegate conflict state updates to conductor via proper events
        publishEvent(CONSTANTS.EVENTS.DATA_VALIDATION_COMPLETED, {
          itemId,
          result,
          isConflicted: result.isConflicted,
          conflictCount: result.conflicts?.length || 0,
          executionTime,
          timestamp: Date.now(),
        });
        publishEvent(CONSTANTS.EVENTS.FORBIDDEN_VALIDATION_COMPLETED, {
          itemId,
          isConflicted: result.isConflicted,
          conflictCount: result.conflicts?.length || 0,
          executionTime,
          timestamp: Date.now(),
          source: 'ForbiddenValidator.validateItemAgainstSelections',
        });

        // Cache result and delegate performance metrics to conductor
        cacheValidationResult(cacheKey, result);

        // ✅ CCP COMPLIANT: Request performance metrics update via conductor delegation
        publishEvent(CONSTANTS.EVENTS.DATA_PERFORMANCE_METRICS_UPDATE, {
          metric: 'averageExecutionTime',
          executionTime,
          timestamp: Date.now(),
        });

        isDev() &&
          console.log('🚫 FORBIDDEN: Validation complete', {
            itemId,
            isConflicted: result.isConflicted,
            conflictCount: result.conflicts?.length || 0,
            executionTime: `${executionTime.toFixed(2)}ms`,
            cacheStored: true,
          });

        return result;
      } catch (error) {
        isDev() &&
          console.error('❌ FORBIDDEN: Validation failed', { itemId, error });

        // ✅ CCP COMPLIANT: Use proper error event namespace
        publishEvent(CONSTANTS.EVENTS.DATA_VALIDATION_FAILED, {
          itemId,
          error: error.message,
          timestamp: Date.now(),
        });

        return {
          isConflicted: false,
          conflicts: [],
          error: error.message,
        };
      }
    },

    /**
     * Internal validation logic
     * @param {string} itemId
     * @param {Object} appliedSelections
     * @param {Object} forbiddenRules
     * @returns {Object}
     */
    /**
     * UNIFIED SYSTEM: Validates an item against unified forbidden rules exclusively.
     * MODERNIZED: All legacy Map references (forbiddenRulesById, itemToForbiddenRuleIds) removed.
     * Uses unified metaobject-driven rules with condition groups and logic operators.
     *
     * @param {string} itemId - Item (variant ID) to validate
     * @param {Map|Object} appliedSelections - Current selections
     * @param {Object} forbiddenRules - Object with { unifiedRules: Map, unifiedConditionGroups: Map }
     * @returns {Object} Validation result { isConflicted: boolean, conflicts: Array }
     */
    _performValidation(itemId, appliedSelections, forbiddenRules) {
      const conflicts = [];

      try {
        // PERFORMANCE OPTIMIZATION: Create productId → selection Map once for O(1) lookups
        const appliedProductSelections = new Map();
        iterateAppliedSelections(appliedSelections, (categoryId, selection) => {
          if (selection?.productId) {
            appliedProductSelections.set(
              String(selection.productId),
              selection
            );
          }
        });

        // Handle missing forbidden rules data gracefully
        if (!forbiddenRules) {
          isDev() &&
            console.warn('❌ FORBIDDEN: Missing forbidden rules data', {
              itemId,
              errorType: 'missing_rules_data',
              recoveryAction: 'Using empty rules fallback',
            });
          return {
            isConflicted: false,
            conflicts: [],
            dataIntegrityIssue: 'missing_rules_data',
          };
        }

        // Validate unified rules structure
        if (
          !forbiddenRules.unifiedRules ||
          !(forbiddenRules.unifiedRules instanceof Map)
        ) {
          isDev() &&
            console.warn('⚠️ FORBIDDEN: No unified rules Map available', {
              itemId,
              hasUnifiedRules: Boolean(forbiddenRules?.unifiedRules),
              isMap: forbiddenRules?.unifiedRules instanceof Map,
            });
          return {
            isConflicted: false,
            conflicts: [],
            dataIntegrityIssue: 'no_unified_rules',
          };
        }

        if (forbiddenRules.unifiedRules.size === 0) {
          isDev() &&
            console.log(
              '⚡ FORBIDDEN: No unified rules configured - no conflicts possible'
            );
          return { isConflicted: false, conflicts: [] };
        }

        // Publish milestone event
        publishEvent(CONSTANTS.EVENTS.DATA_VALIDATION_RULES_RETRIEVED, {
          itemId,
          ruleCount: forbiddenRules.unifiedRules.size,
          timestamp: Date.now(),
          source: 'ForbiddenValidator._performValidation.unified',
        });

        // Publish milestone event before checking conflicts
        publishEvent(
          CONSTANTS.EVENTS.DATA_VALIDATION_CONFLICT_CHECKING_STARTED,
          {
            itemId,
            ruleCount: forbiddenRules.unifiedRules.size,
            timestamp: Date.now(),
            source: 'ForbiddenValidator._performValidation.unified',
          }
        );

        // UNIFIED SYSTEM: Check each rule with BIDIRECTIONAL validation logic
        // Path A: If current item is a target, check if conditions are met
        // Path B: If conditions are met, check if any applied selections are targets
        // PERFORMANCE: Use pre-partitioned conflictRules map (excludes AUTO_APPLY_OPTION rules at bootstrap)
        // This eliminates ~984 runtime "Skipping AUTO_APPLY_OPTION" iterations per session.
        const rulesToCheck =
          forbiddenRules.conflictRules || forbiddenRules.unifiedRules;
        rulesToCheck.forEach((rule, ruleId) => {
          try {
            // 🔥 PATH A: Check if this item is a target variant for this rule
            // Use effective targets (merges targetVariants + targetConditionGroups)
            const effectiveTargets = this._getEffectiveTargetVariants(rule);
            if (effectiveTargets.has(itemId)) {
              const result = this._checkUnifiedRule(
                rule,
                appliedSelections,
                appliedProductSelections
              );

              if (result.hasConflict) {
                conflicts.push({
                  ruleId,
                  rule,
                  reason: result.reason,
                  conflictingSelections: result.conflictingVariants,
                  ruleType: 'unified',
                  category: rule.category,
                  priority: rule.priority,
                  validationPath: 'target_item_checked',
                });

                isDev() &&
                  console.log(
                    '🚨 VALIDATOR: Unified rule conflict detected (Path A: item is target)',
                    {
                      itemId,
                      ruleId,
                      ruleName: rule.name,
                      category: rule.category,
                      reason: result.reason,
                    }
                  );

                // CCP COMPLIANT: Publish milestone event for unified conflict detection
                publishEvent(
                  CONSTANTS.EVENTS.DATA_VALIDATION_CONFLICT_DETECTED,
                  {
                    itemId,
                    ruleId,
                    conflictingSelections: result.conflictingVariants,
                    ruleType: 'unified',
                    category: rule.category,
                    timestamp: Date.now(),
                    source:
                      'ForbiddenValidator._performValidation.unified.pathA',
                  }
                );
              }
            }

            // 🔥 PATH B: Check if NEW itemId is a condition variant that would trigger this rule,
            // then verify if any ALREADY APPLIED selections are target variants
            // O(1) OPTIMIZATION: Use Set for condition variant membership check in hot validation loop
            const conditionVariantIdsSet =
              this._getVariantIdsFromConditionGroups(
                rule.conditionGroups,
                true
              );

            if (conditionVariantIdsSet.has(String(itemId))) {
              // The new itemId is a condition variant! Find which product it belongs to
              // PPD-FIRST: Use conductor for canonical product info instead of brittle appliedSelections inference
              const conductor = window.MurphyDoorConfigurator.conductor;
              const productInfo = conductor
                ? conductor.getProductByVariantId(String(itemId))
                : null;

              if (!productInfo || !productInfo.category) {
                // Safety: If product not found in PPD, skip this validation path
                isDev() &&
                  console.warn(
                    `VALIDATOR: Could not find product info for variant ${itemId} in PPD - skipping Path B validation`
                  );
                return; // Skip this rule's Path B validation if no canonical data
              }

              const itemProductId = String(productInfo.id);
              const categoryId = productInfo.category;

              if (itemProductId) {
                // Create temporary selections that include the new itemId to test if it would trigger the rule
                const tempSelections =
                  appliedSelections instanceof Map
                    ? new Map(appliedSelections)
                    : new Map(Object.entries(appliedSelections || {}));

                // Add the new itemId to temp selections
                tempSelections.set(categoryId, {
                  productId: itemProductId,
                  variantId: itemId,
                });

                // Check if adding this selection would trigger the rule conditions
                // Create temp productId map for tempSelections
                const tempProductSelections = new Map();
                iterateAppliedSelections(tempSelections, (catId, sel) => {
                  if (sel?.productId) {
                    tempProductSelections.set(String(sel.productId), sel);
                  }
                });
                const conditionResult = this._checkUnifiedRule(
                  rule,
                  tempSelections,
                  tempProductSelections
                );

                if (conditionResult.hasConflict) {
                  // Rule would be triggered! Check if any EXISTING applied selections are target variants
                  let foundAppliedTarget = false;

                  // Iterate through ORIGINAL applied selections to find target variants
                  iterateAppliedSelections(
                    appliedSelections,
                    (categoryId, selection) => {
                      const appliedVariantId = String(selection?.variantId);
                      if (
                        appliedVariantId &&
                        effectiveTargets.has(appliedVariantId) &&
                        appliedVariantId !== itemId
                      ) {
                        foundAppliedTarget = true;

                        // Avoid duplicate conflicts - check if not already added from Path A
                        const isDuplicate = conflicts.some(
                          (c) =>
                            c.ruleId === ruleId &&
                            c.conflictingSelections.includes(appliedVariantId)
                        );

                        if (!isDuplicate) {
                          conflicts.push({
                            ruleId,
                            rule,
                            reason: conditionResult.reason,
                            conflictingSelections: [appliedVariantId],
                            ruleType: 'unified',
                            category: rule.category,
                            priority: rule.priority,
                            validationPath: 'condition_triggered',
                          });

                          isDev() &&
                            console.log(
                              '🚨 VALIDATOR: Unified rule conflict detected (Path B: condition triggered)',
                              {
                                itemId,
                                conflictingAppliedVariant: appliedVariantId,
                                ruleId,
                                ruleName: rule.name,
                                category: rule.category,
                                reason: conditionResult.reason,
                              }
                            );

                          // CCP COMPLIANT: Publish milestone event for unified conflict detection
                          publishEvent(
                            CONSTANTS.EVENTS.DATA_VALIDATION_CONFLICT_DETECTED,
                            {
                              itemId,
                              ruleId,
                              conflictingSelections: [appliedVariantId],
                              ruleType: 'unified',
                              category: rule.category,
                              timestamp: Date.now(),
                              source:
                                'ForbiddenValidator._performValidation.unified.pathB',
                            }
                          );
                        }
                      }
                    }
                  );

                  if (foundAppliedTarget) {
                    isDev() &&
                      console.log(
                        '🎯 VALIDATOR: Path B detected conflict - current selection triggers rule that affects already-applied targets',
                        { itemId, ruleId, ruleName: rule.name }
                      );
                  }
                }
              }
            }
          } catch (unifiedRuleError) {
            isDev() &&
              console.warn('❌ FORBIDDEN: Unified rule processing failed', {
                itemId,
                ruleId,
                ruleName: rule.name,
                error: unifiedRuleError.message,
                errorType: 'unified_rule_processing_error',
                recoveryAction: 'Skipping failed unified rule',
              });
            // Continue with other unified rules
          }
        });

        const finalResult = {
          isConflicted: conflicts.length > 0,
          conflicts,
        };

        isDev() &&
          console.log('🏁 FORBIDDEN: Unified validation result', {
            itemId,
            isConflicted: finalResult.isConflicted,
            conflictsCount: conflicts.length,
          });

        return finalResult;
      } catch (validationError) {
        isDev() &&
          console.error('❌ FORBIDDEN: Validation completely failed', {
            itemId,
            error: validationError.message,
            errorType: 'complete_validation_failure',
            recoveryAction: 'Returning safe fallback state',
          });

        // Return safe fallback state
        return {
          isConflicted: false,
          conflicts: [],
          criticalError: validationError.message,
        };
      }
    },

    /**
     * 🚀 PREEMPTIVE FORBIDDEN VALIDATION: Get items that WOULD be forbidden if selected
     *
     * This method performs forward-looking validation to identify items in a target category
     * that would create forbidden combinations if selected, based on current applied selections.
     * This enables preemptive UI disabling of incompatible options.
     *
     * @param {Object} appliedSelections - Current applied selections
     * @param {Object} forbiddenRules - Forbidden combination rules
     * @param {string} targetCategoryId - Category to check for potentially forbidden items
     * @returns {Promise<Set>} Promise resolving to set of item IDs that would be forbidden if selected in target category
     */
    async getPotentiallyForbiddenItems(
      appliedSelections,
      forbiddenRules,
      targetCategoryId
    ) {
      // 🔥 CCP COMPLIANCE: Bootstrap barrier for Phase 2 modules (module-scoped flag, not global)
      if (!forbiddenBootstrapComplete) {
        isDev() &&
          console.warn(
            '🚫 PREEMPTIVE: Bootstrap not complete - deferring validation',
            {
              targetCategoryId,
              bootstrapStatus: forbiddenBootstrapComplete,
            }
          );
        return new Set(); // Return empty set until bootstrap completes
      }

      isDev() &&
        console.log(
          '🚀 CCP-COMPLIANT PREEMPTIVE: Bidirectional forbidden validation started',
          {
            appliedSelectionsCount:
              getAppliedSelectionsCount(appliedSelections),
            targetCategory: targetCategoryId,
            rulesAvailable: Boolean(forbiddenRules),
            appliedSelectionsType:
              appliedSelections instanceof Map ? 'Map' : 'Object',
            bootstrapComplete: forbiddenBootstrapComplete,
          }
        );

      const potentiallyForbiddenItems = new Set();

      if (!forbiddenRules || !appliedSelections || !targetCategoryId) {
        isDev() &&
          console.log(
            '✅ CCP-COMPLIANT PREEMPTIVE: No rules, selections, or target category - returning empty set'
          );
        return potentiallyForbiddenItems;
      }

      // 🔥 CCP-COMPLIANT BIDIRECTIONAL APPROACH: Check both directions efficiently
      await this._checkBidirectionalForbiddenItems(
        appliedSelections,
        forbiddenRules,
        targetCategoryId,
        potentiallyForbiddenItems
      );

      isDev() &&
        console.log(
          '🎉 PREEMPTIVE: CCP-compliant bidirectional validation complete',
          {
            targetCategory: targetCategoryId,
            potentiallyForbiddenCount: potentiallyForbiddenItems.size,
            // First 5 for logging
            forbiddenItems: Array.from(potentiallyForbiddenItems).slice(0, 5),
          }
        );

      return potentiallyForbiddenItems;
    },

    /**
     * Get all items (products and variants) in a specific category
     * CCP-COMPLIANT: Uses event-driven data requests instead of direct conductor access
     * @param {string} categoryId - The category ID to get items for
     * @returns {Promise<Array>} Promise resolving to array of item objects with id, title, type, etc.
     */
    _getCategoryItems(categoryId) {
      isDev() &&
        console.log(
          `🔍 CATEGORY: CCP-compliant getting items for category ${categoryId}`
        );

      // CCP-COMPLIANT: Use event-driven data request instead of direct conductor access
      return new Promise((resolve) => {
        const requestId = `category-items-${categoryId}-${Date.now()}`;

        // Setup response handler
        const handleResponse = (eventData) => {
          if (eventData.requestId === requestId) {
            window.MurphyDoorConfigurator.CoreEventBus.unsubscribe(
              CONSTANTS.EVENTS.DATA_CATEGORY_ITEMS_RESPONSE,
              handleResponse
            );
            resolve(eventData.items || []);
          }
        };

        // Subscribe to response
        window.MurphyDoorConfigurator.CoreEventBus.subscribe(
          CONSTANTS.EVENTS.DATA_CATEGORY_ITEMS_RESPONSE,
          handleResponse
        );

        // Publish data request
        publishEvent(CONSTANTS.EVENTS.DATA_CATEGORY_ITEMS_REQUESTED, {
          requestId,
          categoryId,
          requester: 'forbidden-validator',
        });
      });
    },

    /**
     * 🔥 BIDIRECTIONAL FORBIDDEN VALIDATION: Check both one→many and many→one directions
     *
     * This method efficiently checks forbidden relationships in both directions:
     * - If "one" is applied → disable all "many" items in target category
     * - If any "many" is applied → disable "one" items in target category
     *
     * @param {Object} appliedSelections - Current applied selections
     * @param {Object} forbiddenRules - Forbidden combination rules
     * @param {string} targetCategoryId - Category to check for forbidden items
     * @param {Set} potentiallyForbiddenItems - Set to add forbidden items to
     * @returns {Promise<void>} Promise that resolves when bidirectional check is complete
     */
    async _checkBidirectionalForbiddenItems(
      appliedSelections,
      forbiddenRules,
      targetCategoryId,
      potentiallyForbiddenItems
    ) {
      isDev() &&
        console.log(
          `🔥 BIDIRECTIONAL: Checking category ${targetCategoryId} for forbidden items`
        );

      // Get all items in target category using event-driven approach
      const targetCategoryItems =
        await this._getCategoryItems(targetCategoryId);
      if (!targetCategoryItems || targetCategoryItems.length === 0) {
        isDev() &&
          console.log(
            `⚠️ BIDIRECTIONAL: No items found in category ${targetCategoryId}`
          );
        return;
      }

      isDev() &&
        console.log(
          `🔍 BIDIRECTIONAL: Found ${targetCategoryItems.length} items in category ${targetCategoryId}`
        );

      // UNIFIED SYSTEM: Process unified rules for bidirectional forbidden checking
      // PERFORMANCE: Use pre-partitioned conflictRules map when available (excludes AUTO_APPLY_OPTION)
      const unifiedRules =
        forbiddenRules.conflictRules || forbiddenRules.unifiedRules;
      if (
        !unifiedRules ||
        !(unifiedRules instanceof Map) ||
        unifiedRules.size === 0
      ) {
        isDev() &&
          console.warn(
            `⚠️ BIDIRECTIONAL: No unified rules available for bidirectional checking`
          );
        return;
      }

      isDev() &&
        console.log(
          `🔧 BIDIRECTIONAL: Processing ${unifiedRules.size} conflict rules`,
          {
            forbiddenRulesKeys: Object.keys(forbiddenRules || {}),
          }
        );

      // For each target category item, check if any unified rule would disable it
      const targetItemIds = targetCategoryItems.map((item) => String(item.id));

      // PERFORMANCE OPTIMIZATION: Build productId → selection Map once for O(1) lookups
      const appliedProductSelections = new Map();
      iterateAppliedSelections(appliedSelections, (categoryId, selection) => {
        if (selection?.productId) {
          appliedProductSelections.set(String(selection.productId), selection);
        }
      });

      unifiedRules.forEach((rule, ruleId) => {
        try {
          // Check if any target category items are in this rule's effective targets
          // (merges targetVariants + targetConditionGroups)
          const effectiveTargets = this._getEffectiveTargetVariants(rule);
          targetItemIds.forEach((itemId) => {
            if (effectiveTargets.has(itemId)) {
              // This item could be forbidden - check if conditions are met
              const result = this._checkUnifiedRule(
                rule,
                appliedSelections,
                appliedProductSelections
              );
              if (result.hasConflict) {
                potentiallyForbiddenItems.add(itemId);
                isDev() &&
                  console.log(
                    `🚨 BIDIRECTIONAL: Unified rule "${rule.name}" forbids item ${itemId}`,
                    {
                      ruleId,
                      category: rule.category,
                    }
                  );
              }
            }
          });
        } catch (error) {
          isDev() &&
            console.error(
              `❌ BIDIRECTIONAL: Error processing unified rule ${ruleId}:`,
              error
            );
        }
      });
    },

    /**
     * ✅ CCP COMPLIANT: Get forbidden items for selections (used by UI for graying out)
     * Publishes forbidden state updates via event system for CCP compliance
     * @param {Map|Object} appliedSelections - Applied selections by category (PPD Map format)
     * @param {Object} forbiddenRules - Forbidden rules from PPD
     * @returns {Set} Set of forbidden item IDs
     */
    getForbiddenItems(appliedSelections, forbiddenRules) {
      // ✅ GOLDEN STANDARD: Inline bootstrap guard (Pattern #6)
      if (!forbiddenBootstrapComplete) {
        isDev() &&
          console.warn(
            '🚧 FORBIDDEN: Bootstrap not complete - returning empty forbidden set'
          );
        return new Set();
      }

      isDev() &&
        console.log('🔄 FORBIDDEN: Getting forbidden items (unified system)', {
          appliedSelectionsCount: getAppliedSelectionsCount(appliedSelections),
          rulesAvailable: Boolean(forbiddenRules),
          isMapFormat: appliedSelections instanceof Map,
        });

      const forbiddenItems = new Set();

      if (!forbiddenRules || !appliedSelections) {
        this._publishForbiddenStateUpdate(forbiddenItems, appliedSelections);
        return forbiddenItems;
      }

      // UNIFIED SYSTEM: Use unifiedRules to find all target variants and validate
      const unifiedRules = forbiddenRules.unifiedRules;
      if (
        !unifiedRules ||
        !(unifiedRules instanceof Map) ||
        unifiedRules.size === 0
      ) {
        isDev() &&
          console.log('⚡ FORBIDDEN: No unified rules - no items to forbid');
        this._publishForbiddenStateUpdate(forbiddenItems, appliedSelections);
        return forbiddenItems;
      }

      isDev() &&
        console.log(
          '🔧 FORBIDDEN: Processing unified rules for forbidden items',
          {
            unifiedRulesCount: unifiedRules.size,
          }
        );

      // Collect all unique target variant IDs (including from target condition groups), then validate each
      const allTargetVariantIds = new Set();
      unifiedRules.forEach((rule) => {
        this._getEffectiveTargetVariants(rule).forEach((variantId) => {
          allTargetVariantIds.add(variantId);
        });
      });

      for (const itemId of allTargetVariantIds) {
        const validationResult = this._requestItemValidation(
          itemId,
          appliedSelections,
          forbiddenRules
        );

        if (validationResult.isConflicted) {
          forbiddenItems.add(itemId);
          isDev() &&
            console.log('⚡ FORBIDDEN: Item added to forbidden set', {
              itemId,
              conflictCount: validationResult.conflicts?.length || 0,
              totalForbiddenItems: forbiddenItems.size,
            });
        }
      }

      // Publish forbidden state update via events
      this._publishForbiddenStateUpdate(forbiddenItems, appliedSelections);

      return forbiddenItems;
    },

    /**
     * ✅ CCP COMPLIANT: Event-driven validation request instead of direct method call
     * @param {string} itemId - Item to validate
     * @param {Map|Object} appliedSelections - Current applied selections
     * @param {Object} forbiddenRules - Forbidden rules from PPD
     * @returns {Object} Validation result
     */
    _requestItemValidation(itemId, appliedSelections, forbiddenRules) {
      // ✅ CCP COMPLIANCE: Use existing validateItemAgainstSelections but prepare for event-driven future
      // This is a transitional approach - eventually this should be a published event request
      try {
        return this.validateItemAgainstSelections(
          itemId,
          appliedSelections,
          forbiddenRules
        );
      } catch (error) {
        isDev() &&
          console.error('❌ FORBIDDEN: Item validation failed', {
            itemId,
            error,
          });
        return { isConflicted: false, conflicts: [] };
      }
    },

    /**
     * ✅ CCP COMPLIANT: Publish forbidden state update via event system
     * @param {Set} forbiddenItems - Set of forbidden item IDs
     * @param {Map|Object} appliedSelections - Current applied selections
     */
    _publishForbiddenStateUpdate(forbiddenItems, appliedSelections) {
      // ✅ CCP COMPLIANCE: Publish via event bus instead of direct state manager calls
      if (typeof publishEvent === 'function') {
        publishEvent(CONSTANTS.EVENTS.STATE_UPDATE_FORBIDDEN_STATES, {
          forbiddenItems: Array.from(forbiddenItems),
          appliedSelections:
            appliedSelections instanceof Map
              ? Object.fromEntries(appliedSelections)
              : appliedSelections,
          timestamp: Date.now(),
          source: 'forbidden-validator',
        });

        isDev() &&
          console.log(
            '🔄 FORBIDDEN: Published forbidden state update via events',
            {
              forbiddenCount: forbiddenItems.size,
              appliedSelectionsSize:
                appliedSelections instanceof Map
                  ? appliedSelections.size
                  : Object.keys(appliedSelections || {}).length,
              eventName: CONSTANTS.EVENTS.STATE_UPDATE_FORBIDDEN_STATES,
            }
          );
      } else {
        isDev() &&
          console.warn(
            '⚠️ FORBIDDEN: CoreEventBus not available for state update'
          );
        // ✅ CCP COMPLIANCE: Fallback to legacy method until event bus is ready
        this.updateStateManagerForbiddenStates(
          forbiddenItems,
          appliedSelections
        );
      }
    },

    /**
     * Updates state manager forbidden states tracking
     * @param {Set} forbiddenItems - Set of forbidden item IDs
     * @param {Map|Object} appliedSelections - Current applied selections by category (Map from PPD or legacy Object)
     */
    updateStateManagerForbiddenStates(forbiddenItems, appliedSelections) {
      // ✅ GOLDEN STANDARD: Inline bootstrap guard (no wrapper function)
      if (!forbiddenBootstrapComplete) {
        pendingCalls.push({
          fn: this.updateStateManagerForbiddenStates.bind(this),
          context: null,
          args: [forbiddenItems, appliedSelections],
        });
        return;
      }

      try {
        // ✅ CCP COMPLIANCE: Handle Map and Object structures for appliedSelections
        let categoryKeys = ['global'];
        let primaryCategory = 'global';
        let appliedCount = 0;

        if (appliedSelections) {
          if (appliedSelections instanceof Map) {
            // ✅ PPD Map structure - use Map methods
            categoryKeys = Array.from(appliedSelections.keys());
            appliedCount = appliedSelections.size;
          } else if (typeof appliedSelections === 'object') {
            // Legacy object structure - convert safely
            categoryKeys = Object.keys(appliedSelections);
            appliedCount = categoryKeys.length;
          }
          primaryCategory =
            categoryKeys.length > 0 ? categoryKeys[0] : 'global';
        }

        // ✅ CCP COMPLIANCE: Use event-driven state updates
        publishEvent(CONSTANTS.EVENTS.STATE_UPDATE_FORBIDDEN_STATES, {
          forbiddenItems: Array.from(forbiddenItems),
          appliedSelections:
            appliedSelections instanceof Map
              ? Object.fromEntries(appliedSelections)
              : appliedSelections,
          timestamp: Date.now(),
        });

        // ✅ CCP COMPLIANCE: Record the state change for synchronization tracking
        const newForbiddenStates = {
          forbiddenItems: Array.from(forbiddenItems),
          appliedSelections:
            appliedSelections instanceof Map
              ? Object.fromEntries(appliedSelections)
              : appliedSelections,
          timestamp: Date.now(),
        };

        // No previous state needed for simplified tracking
        _recordStateChange(primaryCategory, null, newForbiddenStates);

        isDev() &&
          console.log('🔄 FORBIDDEN: State manager forbidden states updated', {
            categoryCount: categoryKeys.length,
            primaryCategory,
            forbiddenCount: forbiddenItems.size,
            appliedCount,
            appliedSelectionsType:
              appliedSelections instanceof Map ? 'Map' : 'Object',
          });
      } catch (error) {
        isDev() &&
          console.error(
            '❌ FORBIDDEN: Failed to update state manager forbidden states',
            error
          );
      }
    },

    /**
     * Handle browsing state validation when users click on products/variants
     * @param {Object} eventData - Browsing event data from state manager
     */
    handleBrowsingValidation(eventData) {
      isDev() &&
        console.log('👆 FORBIDDEN: Browsing validation triggered', {
          categoryId: eventData.categoryId,
          productId: eventData.productId,
          variantId: eventData.variantId,
          displayTitle: eventData.displayTitle,
        });

      // ✅ GOLDEN STANDARD: Inline bootstrap guard (no wrapper function)
      if (!forbiddenBootstrapComplete) {
        pendingCalls.push({
          fn: this.handleBrowsingValidation.bind(this),
          context: null,
          args: [eventData],
        });
        return;
      }

      try {
        // Determine the item being validated (product or variant)
        const itemId = eventData.variantId || eventData.productId;
        const itemType = eventData.variantId ? 'variant' : 'product';

        if (!itemId) {
          isDev() &&
            console.warn(
              '⚠️ FORBIDDEN: No valid item ID for browsing validation'
            );
          return { isConflicted: false, conflicts: [] };
        }

        // ✅ CCP COMPLIANCE: Use conductor delegation for data access (primary pattern)
        if (!window.MurphyDoorConfigurator?.conductor) {
          isDev() &&
            console.error(
              '❌ FORBIDDEN: Conductor not available for browsing validation'
            );
          return { isConflicted: false, conflicts: [] };
        }

        // ✅ CCP COMPLIANCE: Get data via conductor delegation
        const stateMaps =
          window.MurphyDoorConfigurator.conductor.getStateMaps();
        const forbiddenRules =
          window.MurphyDoorConfigurator.conductor.getUnifiedForbiddenRules();

        if (!stateMaps) {
          isDev() &&
            console.warn(
              '⚠️ FORBIDDEN: No state maps available for browsing validation'
            );
          return { isConflicted: false, conflicts: [] };
        }

        if (!forbiddenRules) {
          isDev() &&
            console.warn(
              '⚠️ FORBIDDEN: No forbidden rules available for browsing validation'
            );
          return { isConflicted: false, conflicts: [] };
        }

        // ✅ CCP COMPLIANCE: Build applied selections with proper Map handling
        const appliedSelections = new Map();

        // ✅ CCP COMPLIANCE: Handle categoryStates as Map (PPD-compliant)
        if (stateMaps.categoryStates instanceof Map) {
          for (const [categoryId, state] of stateMaps.categoryStates) {
            if (
              state &&
              state.selectionState === 'APPLIED' &&
              state.appliedItem
            ) {
              appliedSelections.set(categoryId, {
                productId: state.appliedItem.productId,
                variantId: state.appliedItem.id,
                appliedItem: state.appliedItem, // Keep for createCacheKey compatibility
              });
            }
          }
        } else if (
          stateMaps.categoryStates &&
          typeof stateMaps.categoryStates === 'object'
        ) {
          // Legacy object format support during transition
          Object.entries(stateMaps.categoryStates).forEach(
            ([categoryId, state]) => {
              if (
                state &&
                state.selectionState === 'APPLIED' &&
                state.appliedItem
              ) {
                appliedSelections.set(categoryId, {
                  productId: state.appliedItem.productId,
                  variantId: state.appliedItem.id,
                  appliedItem: state.appliedItem, // Keep for createCacheKey compatibility
                });
              }
            }
          );
        }

        isDev() &&
          console.log(
            '✅ FORBIDDEN: Processing browsing validation with CCP-compliant data',
            {
              itemId,
              itemType,
              appliedSelectionsCount: appliedSelections.size,
              categoryStatesType:
                stateMaps.categoryStates instanceof Map ? 'Map' : 'Object',
              forbiddenRulesAvailable: Boolean(forbiddenRules),
            }
          );

        // Validate the browsing item against applied selections
        const validationResult = this.validateItemAgainstSelections(
          itemId,
          appliedSelections,
          forbiddenRules
        );

        isDev() &&
          console.log('👆 FORBIDDEN: Browsing validation complete', {
            itemId,
            itemType,
            isConflicted: validationResult.isConflicted,
            conflictCount: validationResult.conflicts?.length || 0,
            appliedSelectionsCount: appliedSelections.size,
          });

        // 🚨 CCP COMPLIANCE: Use proper event namespace for results
        publishEvent(CONSTANTS.EVENTS.STATE_BROWSING_RESULT, {
          itemId,
          itemType,
          categoryId: eventData.categoryId,
          validationResult,
          timestamp: Date.now(),
        });

        return validationResult;
      } catch (error) {
        isDev() &&
          console.error('❌ FORBIDDEN: Browsing validation failed', {
            error: error.message,
            eventData,
          });
        return { isConflicted: false, conflicts: [] };
      }
    },

    /**
     * ✅ CCP COMPLIANT: Request data via event-driven pattern with Promise-based handling
     * @param {string} requestEvent - The event to publish for data request
     * @param {Object} requestData - Data to send with the request
     * @returns {Promise<Object>} Response data from the conductor
     */
    /**
     * ✅ CCP COMPLIANT: Event-driven data request with explicit CONSTANTS.EVENTS.* response event
     * Pattern #19: All event names use centralized constants (no dynamic string derivation)
     * Pattern #2: Uses publishEvent() helper instead of direct CoreEventBus.publish
     *
     * @param {string} requestEvent - CONSTANTS.EVENTS.* request event to publish
     * @param {string} responseEvent - CONSTANTS.EVENTS.* response event to subscribe to
     * @param {Object} requestData - Data to send with the request
     * @returns {Promise<Object>} Response data from the conductor
     */
    _requestDataViaEvent(requestEvent, responseEvent, requestData) {
      return new Promise((resolve, reject) => {
        const requestId =
          requestData.requestId ||
          `forbidden_validator_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const timeout = window.setTimeout(() => {
          // Cleanup listener on timeout
          window.MurphyDoorConfigurator.CoreEventBus?.unsubscribe(
            responseEvent,
            handleResponse
          );
          reject(new Error(`Data request timeout for ${requestEvent}`));
        }, 5000);

        const handleResponse = (data) => {
          if (
            data.requestId === requestId ||
            data.requester === requestData.requester
          ) {
            window.clearTimeout(timeout);
            window.MurphyDoorConfigurator.CoreEventBus?.unsubscribe(
              responseEvent,
              handleResponse
            );
            resolve(data);
          }
        };

        // Subscribe before publishing
        window.MurphyDoorConfigurator.CoreEventBus?.subscribe(
          responseEvent,
          handleResponse
        );

        // ✅ Pattern #2: Use publishEvent() helper
        publishEvent(requestEvent, {
          ...requestData,
          requestId,
        });
      });
    },

    /**
     * ✅ CCP COMPLIANT: Handle applied state validation when users click Apply button
     * Fully event-driven with proper async data requests and Map structure handling
     * @param {Object} eventData - Applied event data from state manager
     */
    handleAppliedValidation(eventData) {
      isDev() &&
        console.log('🚨 FORBIDDEN: Applied validation triggered', {
          categoryId: eventData.categoryId,
          productId: eventData.productId,
          variantId: eventData.variantId,
          displayTitle: eventData.displayTitle,
          appliedSelections:
            eventData.appliedSelections instanceof Map
              ? Object.fromEntries(eventData.appliedSelections)
              : eventData.appliedSelections,
        });

      // ✅ CCP COMPLIANCE: Execute validation asynchronously with proper event-driven data requests
      this._executeAppliedValidationAsync(eventData);
    },

    /**
     * ✅ CCP COMPLIANT: Execute applied validation with proper async event-driven data handling
     * Uses Promise-based pattern to properly wait for conductor responses
     * @param {Object} eventData - Applied event data from state manager
     */
    async _executeAppliedValidationAsync(eventData) {
      try {
        const requestId = `applied-validation-${Date.now()}`;

        // ✅ CCP COMPLIANCE: Request data via explicit CONSTANTS.EVENTS.* (Pattern #19)
        const [appliedSelectionsResponse, forbiddenRulesResponse] =
          await Promise.all([
            this._requestDataViaEvent(
              CONSTANTS.EVENTS.DATA_REQUEST_APPLIED_SELECTIONS,
              CONSTANTS.EVENTS.DATA_APPLIED_SELECTIONS_RESPONSE,
              {
                requester: 'forbidden-validator-applied',
                requestId: `${requestId}-selections`,
                timestamp: Date.now(),
              }
            ),
            this._requestDataViaEvent(
              CONSTANTS.EVENTS.DATA_FORBIDDEN_RULES,
              CONSTANTS.EVENTS.DATA_FORBIDDEN_RULES_RESPONSE,
              {
                requester: 'forbidden-validator-applied',
                requestId: `${requestId}-rules`,
                operation: 'applied_validation',
                itemId: eventData.variantId || eventData.productId,
                timestamp: Date.now(),
              }
            ),
          ]);

        const appliedSelections =
          appliedSelectionsResponse?.appliedSelections ||
          eventData.appliedSelections ||
          new Map();
        let forbiddenRules = forbiddenRulesResponse?.forbiddenRules;

        // UNIFIED SYSTEM: Validate unified rules structure before proceeding
        // If event response lacks unified rules, fallback to conductor delegation
        if (!forbiddenRules || !forbiddenRules.unifiedRules) {
          const conductorRules =
            window.MurphyDoorConfigurator?.conductor?.getUnifiedForbiddenRules();
          if (
            !conductorRules ||
            !conductorRules.unifiedRules ||
            conductorRules.unifiedRules.size === 0
          ) {
            isDev() &&
              console.warn(
                '⚠️ FORBIDDEN: No unified rules available for applied validation'
              );
            return;
          }
          forbiddenRules = conductorRules;
        }

        isDev() &&
          console.log('✅ FORBIDDEN: Applied validation data received', {
            appliedSelectionsType:
              appliedSelections instanceof Map
                ? 'Map'
                : typeof appliedSelections,
            appliedSelectionsSize:
              appliedSelections instanceof Map
                ? appliedSelections.size
                : Object.keys(appliedSelections || {}).length,
            forbiddenRulesValid: Boolean(forbiddenRules),
            unifiedRulesCount: forbiddenRules?.unifiedRules?.size || 0,
          });

        // Validate the newly applied item against all current applied selections
        const itemToValidate = eventData.variantId || eventData.productId;
        if (itemToValidate) {
          const validationResult = this.validateItemAgainstSelections(
            itemToValidate,
            appliedSelections,
            forbiddenRules
          );

          isDev() &&
            console.log('🚨 FORBIDDEN: Applied validation result', {
              itemId: itemToValidate,
              isConflicted: validationResult.isConflicted,
              conflicts: validationResult.conflicts,
              categoryId: eventData.categoryId,
            });

          // If conflicts detected, publish conflict events for UI handling
          if (validationResult.isConflicted) {
            publishEvent(CONSTANTS.EVENTS.STATE_APPLIED_CONFLICT_DETECTED, {
              categoryId: eventData.categoryId,
              itemId: itemToValidate,
              conflicts: validationResult.conflicts,
              appliedSelections:
                appliedSelections instanceof Map
                  ? Object.fromEntries(appliedSelections)
                  : appliedSelections,
              timestamp: Date.now(),
            });

            // ✅ CCP COMPLIANCE: Update UI via proper event namespace
            publishEvent(CONSTANTS.EVENTS.UI_UPDATE_FORBIDDEN_STATE, {
              categoryId: eventData.categoryId,
              validationResult,
              timestamp: Date.now(),
            });
          }
        } else {
          isDev() &&
            console.warn(
              '⚠️ FORBIDDEN: No item to validate in applied event data',
              eventData
            );
        }
      } catch (error) {
        isDev() &&
          console.error(
            '❌ FORBIDDEN: Applied validation async execution failed',
            {
              error: error.message,
              eventData,
              stack: error.stack,
            }
          );

        // Route through centralized error handler (DRY: single publication point)
        window.MurphyDoorConfigurator.utils.handleSystemError({
          type: 'ValidationError',
          error: error,
          source: 'forbiddenValidator._executeAppliedValidationAsync',
          recoverable: true,
          impact: 'medium',
          details: { eventData: eventData },
        });
      }
    },

    /**
     * Initialize the forbidden validator module
     */
    initialize() {
      // ✅ GOLDEN STANDARD: Inline bootstrap guard (no wrapper function)
      if (!forbiddenBootstrapComplete) {
        pendingCalls.push({
          fn: this.initialize.bind(this),
          context: null,
          args: [],
        });
        return;
      }

      // PATTERN #16: Initialization Guard - prevent double initialization
      if (isInitialized) {
        isDev() &&
          console.warn('[ForbiddenValidator] Already initialized, skipping');
        return;
      }

      isDev() &&
        console.log('🔍 FORBIDDEN: Validation engine initialized', {
          capabilities: [
            'Product/variant conflict detection',
            'Variant-to-variant AND logic validation',
            'Cross-category conflict analysis',
            'Bidirectional conflict resolution',
            'Performance monitoring',
          ],
        });

      // 📡 TASK 1.14: Console log for comprehensive event-driven communication
      isDev() &&
        console.log('📡 FORBIDDEN: Event communication active', {
          eventTypes: [
            CONSTANTS.EVENTS.FORBIDDEN_CONFLICT_DETECTED,
            CONSTANTS.EVENTS.FORBIDDEN_CONFLICT_RESOLVED,
            CONSTANTS.EVENTS.FORBIDDEN_VALIDATION_STARTED,
            CONSTANTS.EVENTS.FORBIDDEN_VALIDATION_COMPLETED,
          ],
          frequency: 'Real-time during validation',
          features: [
            'Conflict state broadcasting',
            'Validation lifecycle events',
            'Performance monitoring events',
          ],
        });

      // ❌ TASK 1.15: Console log for robust error handling system
      isDev() &&
        console.log('❌ FORBIDDEN: Error handling active', {
          errorTypes: [
            'missing_rules_data',
            'malformed_rules_structure',
            'missing_rule_reference',
            'invalid_product_references',
            'rule_processing_error',
          ],
          recoveryActions: [
            'Empty rules fallback',
            'Skip invalid rules',
            'Safe fallback state',
            'Continue processing valid rules',
          ],
          features: [
            'Data integrity validation',
            'Graceful degradation',
            'Comprehensive error logging',
            'Reference validation',
          ],
        });

      // ⚡ TASK 1.16: Console log for performance optimization system
      isDev() &&
        console.log('⚡ FORBIDDEN: Performance optimization active', {
          timingMetrics: {
            performanceThreshold: `${FORBIDDEN_CONSTANTS.PERFORMANCE_THRESHOLD}ms`,
            cacheExpiry: `${FORBIDDEN_CONSTANTS.CACHE_DURATION}ms`,
            batchThreshold: FORBIDDEN_CONSTANTS.BATCH_SIZE_THRESHOLD,
          },
          features: [
            'Debounced validation for rapid interactions',
            'Result caching with TTL expiry',
            'Optimized bidirectional map lookups',
            'Performance metrics tracking',
            'Cache hit rate optimization',
          ],
          // ✅ CCP COMPLIANT: Request metrics via conductor delegation
          metrics: 'Available via conductor getPerformanceMetrics()',
        });

      // 🔄 TASK 1.17: Console log for state synchronization system and start monitoring
      isDev() &&
        console.log('🔄 FORBIDDEN: State synchronization verified', {
          consistencyChecks: {
            interval: `${FORBIDDEN_CONSTANTS.STATE_SYNC_INTERVAL}ms`,
            recoveryTimeout: `${FORBIDDEN_CONSTANTS.RECOVERY_TIMEOUT}ms`,
            maxCircularDepth: FORBIDDEN_CONSTANTS.MAX_CIRCULAR_DEPTH,
          },
          features: [
            'Circular conflict detection and resolution',
            'Complex constraint chain handling',
            'Automatic state recovery mechanisms',
            'State consistency monitoring',
            'Historical state tracking',
          ],
          // ✅ CCP COMPLIANT: Request metrics via conductor delegation
          metrics: 'Available via conductor getSyncMetrics()',
        });

      // Start state synchronization monitoring
      startStateSynchronization();

      // PATTERN #16: Mark as initialized
      isInitialized = true;

      publishEvent(CONSTANTS.EVENTS.MODULE_FORBIDDEN_VALIDATOR_READY, {
        module: 'forbidden-validator',
        timestamp: Date.now(),
      });
    },

    /**
     * PATTERN #15: Cleanup/destroy method for resource management
     * Unsubscribes all event handlers and resets module state
     */
    destroy() {
      isDev() &&
        console.log(
          '[ForbiddenValidator] Destroying module - cleaning up resources'
        );

      // Unsubscribe all tracked event handlers
      cleanupHandlers.forEach((unsubscribe) => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      });
      cleanupHandlers.length = 0;

      // Clear caches and state
      performanceCache.clear();
      _currentPreemptiveForbiddenState.clear();
      _soldOutEntries.clear();
      stateSyncHistory.clear();
      pendingCalls = [];

      isInitialized = false;
      forbiddenBootstrapComplete = false;

      publishEvent(CONSTANTS.EVENTS.MODULE_FORBIDDEN_VALIDATOR_CLEANUP, {
        module: 'forbidden-validator',
        timestamp: Date.now(),
      });
    },

    /**
     * ===============================================================
     * UNIFIED SYSTEM: Main Unified Rule Checker
     * ===============================================================
     * Pure metadata-driven - NO hardcoded business logic!
     *
     * @param {Object} rule - Unified rule from metaobject
     * @param {Map} appliedSelections - Current user selections
     * @param {Map} appliedProductSelections - Map of productId → selection for O(1) lookups
     * @returns {Object} Conflict result { hasConflict: boolean, reason: string, conflictingVariants: Array }
     */
    _checkUnifiedRule(rule, appliedSelections, appliedProductSelections) {
      isDev() &&
        console.log(`VALIDATOR: Evaluating unified rule "${rule.name}"`, {
          category: rule.category,
          logicOperator: rule.logicOperator,
          conditionCount: rule.conditionGroups.length,
        });

      // Evaluate each condition group
      const evaluations = rule.conditionGroups.map((group) =>
        this._evaluateConditionGroup(
          group,
          appliedSelections,
          appliedProductSelections
        )
      );

      // Apply logic operator (ALL, ANY, NONE)
      const hasConflict = this._applyLogicOperator(
        evaluations,
        rule.logicOperator
      );

      // Build human-readable reason
      const reason = hasConflict
        ? rule.description +
          '\n\nConditions: ' +
          rule.conditionGroups
            .map(function (g) {
              return g.name;
            })
            .join(', ')
        : null;

      isDev() &&
        console.log(
          `VALIDATOR: Unified rule result: ${hasConflict ? 'CONFLICT' : 'PASS'}`,
          {
            ruleName: rule.name,
            category: rule.category,
            evaluations,
            logicOperator: rule.logicOperator,
          }
        );

      return {
        hasConflict: hasConflict,
        reason: reason,
        conflictingVariants: hasConflict
          ? Array.from(this._getEffectiveTargetVariants(rule))
          : [],
      };
    },

    /**
     * Evaluate a single condition group against current applied selections.
     * Metadata-driven: Checks if the user's selection for the group's product
     * matches the group's required variants based on the matchType.
     *
     * @param {Object} group - Condition group { matchType, productId, variantIds, minRange, maxRange }
     * @param {Map|Object} appliedSelections - Current user selections (categoryId -> selection)
     * @param {Map} appliedProductSelections - Map of productId → selection for O(1) lookups
     * @returns {boolean} True if the condition group is satisfied (match found)
     */
    _evaluateConditionGroup(
      group,
      appliedSelections,
      appliedProductSelections
    ) {
      try {
        if (!group || !appliedSelections) {
          return false;
        }

        // ARCHITECTURAL ENHANCEMENT: Support optional check_product for cross-product variant checks
        // If productId is null/undefined, check required_variants directly across ALL products.
        // This enables category-wide checks (e.g., "ANY upper cabinet door" across 31 products)
        // without needing separate condition groups per product.
        if (
          !group.productId ||
          group.productId === 'null' ||
          String(group.productId) === 'null'
        ) {
          isDev() &&
            console.log(
              `VALIDATOR: Condition group "${group.name}" - cross-product check (productId: null)`
            );

          // Check if ANY required variant is selected across ALL products
          // O(1) OPTIMIZATION: Use Set for membership checks in hot validation loop
          const requiredVariantIdsArray = (group.variantIds || []).map(String);
          const requiredVariantIdsSet = new Set(requiredVariantIdsArray);

          // DEBUG: Log what we're checking
          isDev() &&
            console.log(
              `🔍 CROSS-PRODUCT DEBUG: Checking ${requiredVariantIdsSet.size} required variants`,
              { requiredVariantIds: requiredVariantIdsArray.slice(0, 5) } // Show first 5
            );
          isDev() &&
            console.log(
              `🔍 CROSS-PRODUCT DEBUG: Checking ${Object.keys(appliedSelections).length} applied selections`,
              { categoryIds: Object.keys(appliedSelections) }
            );

          // Iterate through all applied selections to find a match
          // Use iterateAppliedSelections helper to handle both Map and Object types
          let matchResult = null;
          iterateAppliedSelections(
            appliedSelections,
            (categoryId, selection) => {
              if (matchResult !== null) return; // Skip if already found

              if (selection && selection.variantId) {
                const selectedVariantId = String(selection.variantId);

                // DEBUG: Log each comparison
                isDev() &&
                  console.log(
                    `🔍 CROSS-PRODUCT DEBUG: Checking category "${categoryId}" variant ${selectedVariantId}`
                  );

                // Check if this selected variant matches any required variant (O(1) Set lookup)
                if (requiredVariantIdsSet.has(selectedVariantId)) {
                  isDev() &&
                    console.log(
                      `VALIDATOR: Condition group "${group.name}" - MATCH FOUND: variant ${selectedVariantId} in category ${categoryId}`
                    );
                  matchResult = this._evaluateMatchType(
                    group.matchType,
                    selectedVariantId,
                    requiredVariantIdsSet,
                    group
                  );
                }
              } else {
                // DEBUG: Log when selection has no variantId
                isDev() &&
                  console.log(
                    `🔍 CROSS-PRODUCT DEBUG: Category "${categoryId}" has no variantId`,
                    { selection }
                  );
              }
            }
          );

          // If a match was found, return the result
          if (matchResult !== null) {
            return matchResult;
          }

          // No matching variant found across any product
          isDev() &&
            console.log(
              `VALIDATOR: Condition group "${group.name}" - no matching variants found across products`
            );
          return false;
        }

        // EXISTING BEHAVIOR: If productId is set, check that specific product (backward compatible)
        // PERFORMANCE OPTIMIZATION: Use Map for O(1) lookup instead of iterating all selections
        const selectedInfo = appliedProductSelections.get(
          String(group.productId)
        );
        const selectedVariantId = selectedInfo
          ? String(selectedInfo.variantId)
          : null;

        // If no selection found for this product, condition cannot be met
        if (!selectedVariantId) {
          isDev() &&
            console.log(
              `VALIDATOR: Condition group "${group.name}" - no selection for product ${group.productId}`
            );
          return false;
        }

        // O(1) OPTIMIZATION: Use Set for membership checks in _evaluateMatchType
        const requiredVariantIds = new Set(
          (group.variantIds || []).map(String)
        );

        return this._evaluateMatchType(
          group.matchType,
          selectedVariantId,
          requiredVariantIds,
          group
        );
      } catch (error) {
        isDev() &&
          console.error(
            `VALIDATOR: Error evaluating condition group "${group?.name}":`,
            error
          );
        return false;
      }
    },

    /**
     * Evaluate matchType logic (extracted for reuse in cross-product and single-product checks).
     * PERFORMANCE: Accepts Set or Array for requiredVariantIds. Set is preferred for O(1) lookups.
     * Range-based match types (RANGE_INSIDE, RANGE_OUTSIDE) require the original Array for indexOf,
     * so callers should pass the array form for those match types.
     *
     * @param {string} matchType - Match type (EQUAL, ANY_IN_LIST, etc.)
     * @param {string} selectedVariantId - The selected variant ID
     * @param {Set<string>|Array<string>} requiredVariantIds - Required variant IDs for matching
     * @param {Object} group - Condition group (for range checks, includes .variantIds for indexOf)
     * @returns {boolean} True if match criteria is met
     */
    _evaluateMatchType(
      matchType,
      selectedVariantId,
      requiredVariantIds,
      group
    ) {
      // O(1) OPTIMIZATION: Convert to Set if not already one (for non-range match types)
      const requiredSet =
        requiredVariantIds instanceof Set
          ? requiredVariantIds
          : new Set(requiredVariantIds);

      switch (matchType) {
        case 'EQUAL':
          // Selected variant must be one of the required variants (exact match)
          return requiredSet.has(selectedVariantId);

        case 'NOT_EQUAL':
          // Selected variant must NOT be one of the required variants
          return !requiredSet.has(selectedVariantId);

        case 'ANY_IN_LIST':
          // Selected variant matches any in the required list
          return requiredSet.has(selectedVariantId);

        case 'ALL_IN_PRODUCT':
          // "Every variant of check_product". Membership is identical to ANY_IN_LIST —
          // the difference is upstream: md-config-core-data-pipeline.js expands the set
          // from the product's LIVE variants at PPD build time instead of a hand-written
          // list, so a variant added after the rule was authored is still covered.
          // Deliberately a separate case rather than folded into ANY_IN_LIST: this is the
          // only place the intent is visible at evaluation time, and falling through to
          // `default` would return false and silently disable the rule.
          return requiredSet.has(selectedVariantId);

        case 'NONE_IN_LIST':
          // Selected variant must not be in the required list
          return !requiredSet.has(selectedVariantId);

        case 'RANGE_INSIDE':
          // Selected variant index falls within min/max range
          // NOTE: Range checks need Array for indexOf (positional lookup)
          if (group.minRange != null && group.maxRange != null) {
            const variantArray = Array.isArray(requiredVariantIds)
              ? requiredVariantIds
              : (group.variantIds || []).map(String);
            const variantIndex = variantArray.indexOf(selectedVariantId);
            return (
              variantIndex >= group.minRange && variantIndex <= group.maxRange
            );
          }
          return requiredSet.has(selectedVariantId);

        case 'RANGE_OUTSIDE':
          // Selected variant index falls outside min/max range
          // NOTE: Range checks need Array for indexOf (positional lookup)
          if (group.minRange != null && group.maxRange != null) {
            const variantArray = Array.isArray(requiredVariantIds)
              ? requiredVariantIds
              : (group.variantIds || []).map(String);
            const variantIndex = variantArray.indexOf(selectedVariantId);
            return (
              variantIndex === -1 ||
              variantIndex < group.minRange ||
              variantIndex > group.maxRange
            );
          }
          return !requiredSet.has(selectedVariantId);

        default:
          // Returning false FAILS OPEN: the condition never matches, so the rule never
          // fires and the forbidden combination becomes selectable. That is the safer
          // default for an unrecognised type (a typo must not block every configuration),
          // but it is silent in production, so make the dev warning name the fix.
          isDev() &&
            console.warn(
              `VALIDATOR: Unknown matchType "${matchType}" for condition group "${group?.name}" — this rule will NEVER fire. Valid: EQUAL, NOT_EQUAL, ANY_IN_LIST, NONE_IN_LIST, ALL_IN_PRODUCT, RANGE_INSIDE, RANGE_OUTSIDE.`
            );
          return false;
      }
    },

    /**
     * Apply logic operator across condition group evaluations.
     * Combines individual condition group results using ALL or ANY logic.
     *
     * @param {boolean[]} evaluations - Array of boolean results from _evaluateConditionGroup
     * @param {string} logicOperator - 'ALL_MUST_MATCH' or 'ANY_MUST_MATCH'
     * @returns {boolean} True if the combined evaluation indicates a conflict
     */
    _applyLogicOperator(evaluations, logicOperator) {
      if (!evaluations || evaluations.length === 0) {
        return false;
      }

      switch (logicOperator) {
        case 'ALL_MUST_MATCH':
          // All condition groups must be satisfied for a conflict
          return evaluations.every(Boolean);

        case 'ANY_MUST_MATCH':
          // Any single condition group being satisfied means conflict
          return evaluations.some(Boolean);

        default:
          isDev() &&
            console.warn(
              `VALIDATOR: Unknown logic operator "${logicOperator}", defaulting to ALL_MUST_MATCH`
            );
          return evaluations.every(Boolean);
      }
    },

    /**
     * Recover state when inconsistencies are detected
     * CCP-COMPLIANT: Fully event-driven async pattern with proper data handling
     */
    recoverState(targetCategoryId) {
      return recoverState(targetCategoryId);
    },

    // =========================================================================
    // BIDIRECTIONAL PREEMPTIVE FORBIDDEN VALIDATION
    // =========================================================================
    // Calculates which items should be DISABLED across ALL categories based on
    // current applied selections. Supports conflict_direction field:
    //   - BIDIRECTIONAL (default): conditions disable targets AND targets disable conditions
    //   - ONE_WAY: only conditions disable targets (original behavior)
    //   - MUTUAL_EXCLUSION: all items in the rule disable each other
    // =========================================================================

    /**
     * Extract variant IDs from condition groups (unified helper).
     * Works for both condition-side groups (what triggers a rule) and target-side
     * groups (what gets disabled). Consolidates the previously duplicated
     * _extractVariantIdsFromConditionGroups and _expandTargetConditionGroups methods.
     *
     * @param {Array} conditionGroups - Array of condition group objects (condition or target side)
     * @param {boolean} [returnAsSet=false] - If true, returns a Set for O(1) lookups instead of Array
     * @returns {Array<string>|Set<string>} Variant IDs from condition groups
     */
    _getVariantIdsFromConditionGroups(conditionGroups, returnAsSet) {
      if (
        !conditionGroups ||
        !Array.isArray(conditionGroups) ||
        conditionGroups.length === 0
      ) {
        return returnAsSet ? new Set() : [];
      }

      const variantIds = [];
      conditionGroups.forEach(function (group) {
        if (group && Array.isArray(group.variantIds)) {
          group.variantIds.forEach(function (vid) {
            variantIds.push(String(vid));
          });
        }
      });

      return returnAsSet ? new Set(variantIds) : variantIds;
    },

    /**
     * Get the effective (combined) set of target variant IDs for a rule.
     * Merges explicit targetVariants with variants expanded from targetConditionGroups.
     * Returns a Set for O(1) lookups and deduplication, consistent with rule.targetVariants.
     *
     * Backward compatible: rules with only targetVariants return those as-is.
     * Rules with only targetConditionGroups return expanded variants.
     * Rules with both return the union (deduplicated).
     *
     * @param {Object} rule - The unified rule object
     * @returns {Set<string>} Combined set of all target variant IDs
     */
    _getEffectiveTargetVariants(rule) {
      // Start with the explicit targetVariants (always a Set)
      const effectiveSet = new Set(rule.targetVariants);

      // Expand and merge target condition groups if present
      if (rule.targetConditionGroups && rule.targetConditionGroups.length > 0) {
        const expandedVariants = this._getVariantIdsFromConditionGroups(
          rule.targetConditionGroups
        );
        for (let i = 0; i < expandedVariants.length; i++) {
          effectiveSet.add(expandedVariants[i]);
        }
      }

      return effectiveSet;
    },

    /**
     * Add an item to the disable map, organized by the item's variant ID.
     * Each entry stores the rule metadata for tooltip/logging purposes.
     *
     * @param {string} itemId - Variant ID to disable
     * @param {Object} rule - The rule that causes the disabling
     * @param {Map} disableMap - Map of variantId -> [{reason, ruleId, ruleName}]
     */
    _addToDisableMap(itemId, rule, disableMap) {
      if (!disableMap.has(itemId)) {
        disableMap.set(itemId, []);
      }

      // Avoid duplicate entries for the same rule
      const existingEntries = disableMap.get(itemId);
      const isDuplicate = existingEntries.some(
        (entry) => entry.ruleId === rule.id
      );

      if (!isDuplicate) {
        existingEntries.push({
          ruleId: rule.id,
          ruleName: rule.name,
          reason: rule.description || rule.name,
          category: rule.category,
          priority: rule.priority,
        });
      }
    },

    /**
     * Process a ONE_WAY rule: only conditions disable targets.
     * This is the original behavior before bidirectional support.
     *
     * @param {Object} rule - The unified rule
     * @param {Map|Object} appliedSelections - Current applied selections
     * @param {Map} disableMap - Map to add disabled items to
     * @param {Set} appliedVariantIdsSet - Set of currently applied variant IDs for O(1) lookups
     * @param {Map} appliedProductSelections - Map of productId → selection for O(1) lookups
     */
    _processOneWayPreemptiveRule(
      rule,
      appliedSelections,
      disableMap,
      appliedVariantIdsSet,
      appliedProductSelections
    ) {
      // Check if any condition group variants are applied
      const conditionResult = this._checkUnifiedRule(
        rule,
        appliedSelections,
        appliedProductSelections
      );

      if (conditionResult.hasConflict) {
        // Conditions are met -> disable all effective target variants
        // (merges targetVariants + targetConditionGroups)
        this._getEffectiveTargetVariants(rule).forEach((targetId) => {
          // Don't disable items that are already applied (they were applied before the conflict)
          if (!appliedVariantIdsSet.has(String(targetId))) {
            this._addToDisableMap(targetId, rule, disableMap);
          }
        });
      }
    },

    /**
     * Process a BIDIRECTIONAL rule: conditions disable targets AND targets disable conditions.
     * This is the DEFAULT behavior - when depth 5-1/4" is selected, In-Swing options are disabled,
     * AND when In-Swing is selected, depth 5-1/4" is disabled.
     *
     * @param {Object} rule - The unified rule
     * @param {Map|Object} appliedSelections - Current applied selections
     * @param {Map} disableMap - Map to add disabled items to
     * @param {Set} appliedVariantIdsSet - Set of currently applied variant IDs for O(1) lookups
     * @param {Map} appliedProductSelections - Map of productId → selection for O(1) lookups
     */
    _processBidirectionalPreemptiveRule(
      rule,
      appliedSelections,
      disableMap,
      appliedVariantIdsSet,
      appliedProductSelections
    ) {
      const conditionVariantIds = this._getVariantIdsFromConditionGroups(
        rule.conditionGroups
      );
      // Use effective targets (merges targetVariants + targetConditionGroups)
      const targetVariantIds = Array.from(
        this._getEffectiveTargetVariants(rule)
      );

      // Direction A: If condition variants are applied -> disable all target variants
      const conditionResult = this._checkUnifiedRule(
        rule,
        appliedSelections,
        appliedProductSelections
      );

      if (conditionResult.hasConflict) {
        targetVariantIds.forEach((targetId) => {
          if (!appliedVariantIdsSet.has(String(targetId))) {
            this._addToDisableMap(targetId, rule, disableMap);
          }
        });
      }

      // Direction B (REVERSE): If any target variant is applied -> disable all condition variants
      const hasAppliedTarget = targetVariantIds.some((targetVarId) =>
        appliedVariantIdsSet.has(String(targetVarId))
      );

      if (hasAppliedTarget) {
        conditionVariantIds.forEach((conditionId) => {
          if (!appliedVariantIdsSet.has(String(conditionId))) {
            this._addToDisableMap(conditionId, rule, disableMap);
          }
        });
      }
    },

    /**
     * Process a MUTUAL_EXCLUSION rule: all items in the rule disable each other.
     * If ANY item from the combined set (conditions + targets) is applied,
     * ALL OTHER items in the set are disabled.
     *
     * @param {Object} rule - The unified rule
     * @param {Map|Object} appliedSelections - Current applied selections
     * @param {Map} disableMap - Map to add disabled items to
     * @param {Set} appliedVariantIdsSet - Set of currently applied variant IDs for O(1) lookups
     * @param {Map} appliedProductSelections - Map of productId → selection for O(1) lookups
     */
    _processMutualExclusionPreemptiveRule(
      rule,
      appliedSelections,
      disableMap,
      appliedVariantIdsSet,
      appliedProductSelections
    ) {
      const conditionVariantIds = this._getVariantIdsFromConditionGroups(
        rule.conditionGroups
      );
      // Use effective targets (merges targetVariants + targetConditionGroups)
      const targetVariantIds = Array.from(
        this._getEffectiveTargetVariants(rule)
      );

      // Combine all variant IDs into one pool
      const allVariantIds = conditionVariantIds.concat(targetVariantIds);

      // Find which ones are applied
      const appliedVariantIds = allVariantIds.filter((vid) =>
        appliedVariantIdsSet.has(String(vid))
      );

      // If any are applied, disable all others
      if (appliedVariantIds.length > 0) {
        allVariantIds.forEach((vid) => {
          if (!appliedVariantIdsSet.has(String(vid))) {
            this._addToDisableMap(vid, rule, disableMap);
          }
        });
      }
    },

    // ==========================================================================
    // AUTO_APPLY_OPTION: Evaluation methods for auto-apply forbidden rules
    // ==========================================================================

    /**
     * Evaluate AUTO_APPLY_OPTION rules when a variant is APPLIED.
     * Finds all AUTO_APPLY_OPTION rules where the applied variant satisfies conditions,
     * then resolves the best material-matching target variant to auto-apply.
     *
     * PATTERN 14: Try/Catch + Failed Events
     * PATTERN 7: Perfect Dictionary Only (PPD-First via variantObjectMap)
     *
     * @param {string} appliedVariantId - The variant ID that was just applied
     * @param {Map|Object} appliedSelections - Current applied selections (categoryId -> selection)
     * @param {Object} forbiddenRules - Unified forbidden rules { unifiedRules: Map }
     * @param {Map} variantObjectMap - PPD's variantIdToVariantObjectMap for material matching
     * @returns {Array} Array of { ruleId, ruleName, targetVariantId, targetCategoryId, reason }
     */
    evaluateAutoApplyRulesOnApply(
      appliedVariantId,
      appliedSelections,
      forbiddenRules,
      variantObjectMap
    ) {
      const startTime = performance.now();
      const results = [];

      try {
        if (
          !appliedVariantId ||
          !forbiddenRules?.unifiedRules ||
          !(forbiddenRules.unifiedRules instanceof Map)
        ) {
          return results;
        }

        if (!variantObjectMap || !(variantObjectMap instanceof Map)) {
          isDev() &&
            console.warn(
              '[ForbiddenValidator] evaluateAutoApplyRulesOnApply: variantObjectMap not available'
            );
          return results;
        }

        const appliedVariantIdStr = String(appliedVariantId);

        // Build O(1) lookup for productId -> selection (composite-aware)
        const appliedProductSelections = this._buildAppliedProductSelections(
          appliedSelections,
          variantObjectMap
        );

        // Get source variant object for material matching
        const numericAppliedId = parseInt(appliedVariantIdStr, 10);
        const sourceVariant =
          variantObjectMap.get(numericAppliedId) ||
          variantObjectMap.get(appliedVariantIdStr);

        // PERFORMANCE: Use pre-partitioned autoApplyRules map when available (only AUTO_APPLY_OPTION rules)
        // Falls back to unifiedRules with manual filtering when not available.
        const autoApplyRulesToCheck =
          forbiddenRules.autoApplyRules instanceof Map
            ? forbiddenRules.autoApplyRules
            : forbiddenRules.unifiedRules;

        autoApplyRulesToCheck.forEach((rule, ruleId) => {
          try {
            // Only process AUTO_APPLY_OPTION rules (guard needed only when using unifiedRules fallback)
            if (rule.targetAction !== 'AUTO_APPLY_OPTION') {
              return;
            }

            // ENFORCED_PAIR rules are evaluated by evaluateEnforcedPairActions()
            // instead (force over manual picks + reverse clear). Skipping them
            // here guarantees one rule never produces two auto-apply requests.
            if (this._isEnforcedPairRule(rule)) {
              return;
            }

            // Check if the applied variant is a condition variant for this rule
            // O(1) OPTIMIZATION: Use Set for condition variant membership check
            const conditionVariantIdsSet =
              this._getVariantIdsFromConditionGroups(
                rule.conditionGroups,
                true
              );
            if (!conditionVariantIdsSet.has(appliedVariantIdStr)) {
              return; // Applied variant is not a condition for this rule
            }

            // Evaluate the rule conditions against current selections
            const conditionResult = this._checkUnifiedRule(
              rule,
              appliedSelections,
              appliedProductSelections
            );

            if (!conditionResult.hasConflict) {
              // "hasConflict" means conditions are satisfied (reusing existing logic)
              // For AUTO_APPLY, satisfied conditions mean we SHOULD auto-apply
              return;
            }

            // Find the best material-matching target variant and resolve its
            // PPD category/product (shared with the ENFORCED_PAIR evaluator)
            const resolvedTarget = this._resolveAutoApplyTarget(
              rule,
              sourceVariant,
              appliedSelections,
              variantObjectMap
            );
            if (!resolvedTarget) {
              return;
            }
            const targetVariantId = resolvedTarget.targetVariantId;
            const targetCategoryId = resolvedTarget.targetCategoryId;
            const targetProductId = resolvedTarget.targetProductId;

            results.push({
              ruleId: ruleId,
              ruleName: rule.name,
              ruleDescription: rule.description,
              targetVariantId: String(targetVariantId),
              targetCategoryId: targetCategoryId,
              targetProductId: targetProductId,
              conditionVariantId: appliedVariantIdStr,
              reason:
                rule.description ||
                'Auto-apply triggered by rule: ' + rule.name,
              priority: rule.priority,
            });

            isDev() &&
              console.log(
                '[ForbiddenValidator] AUTO_APPLY: Rule "' +
                  rule.name +
                  '" matched',
                {
                  conditionVariantId: appliedVariantIdStr,
                  targetVariantId: String(targetVariantId),
                  targetCategoryId: targetCategoryId,
                  sourceMaterial: sourceVariant
                    ? {
                        wood_sku: sourceVariant.wood_sku,
                        finish_sku: sourceVariant.finish_sku,
                      }
                    : 'unknown',
                }
              );
          } catch (ruleError) {
            isDev() &&
              console.error(
                '[ForbiddenValidator] AUTO_APPLY: Error processing rule "' +
                  (rule.name || ruleId) +
                  '":',
                ruleError
              );
          }
        });

        const executionTime = performance.now() - startTime;

        if (executionTime > FORBIDDEN_CONSTANTS.PERFORMANCE_THRESHOLD) {
          publishEvent(CONSTANTS.EVENTS.PERFORMANCE_AUTO_APPLY_SLOW, {
            module: 'forbidden-validator',
            method: 'evaluateAutoApplyRulesOnApply',
            executionTime: executionTime,
            threshold: FORBIDDEN_CONSTANTS.PERFORMANCE_THRESHOLD,
            resultsCount: results.length,
            timestamp: Date.now(),
          });
        }

        isDev() &&
          console.log('[ForbiddenValidator] AUTO_APPLY evaluation complete', {
            appliedVariantId: appliedVariantIdStr,
            matchingRules: results.length,
            executionTime: executionTime.toFixed(2) + 'ms',
          });

        return results;
      } catch (error) {
        isDev() &&
          console.error(
            '[ForbiddenValidator] evaluateAutoApplyRulesOnApply failed:',
            error
          );
        publishEvent(CONSTANTS.EVENTS.FORBIDDEN_AUTO_APPLY_FAILED, {
          error: error.message,
          appliedVariantId: String(appliedVariantId),
          source: 'ForbiddenValidator.evaluateAutoApplyRulesOnApply',
          timestamp: Date.now(),
        });
        return results;
      }
    },

    /**
     * Build the productId -> selection lookup used by condition-group evaluation.
     * Indexes BOTH the stored (possibly composite) productId and the raw Shopify
     * product id from the PPD variant, so condition groups keyed by raw ids match.
     *
     * @param {Map|Object} appliedSelections - Current applied selections
     * @param {Map} variantObjectMap - PPD variantIdToVariantObjectMap
     * @returns {Map<string, Object>} productId -> selection
     */
    _buildAppliedProductSelections(appliedSelections, variantObjectMap) {
      // Build O(1) lookup for productId -> selection
      // CRITICAL FIX: For composite products, the state stores composite product IDs
      // (e.g., "composite-top-pocket-lipped-shelves") but condition groups reference
      // raw Shopify product IDs (e.g., "8240227123245"). We must index by BOTH
      // the composite ID AND the raw Shopify product_id from each variant's PPD entry,
      // so _evaluateConditionGroup can find selections via raw Shopify product IDs.
      const appliedProductSelections = new Map();
      iterateAppliedSelections(appliedSelections, (categoryId, selection) => {
        if (selection?.productId) {
          appliedProductSelections.set(String(selection.productId), selection);

          // COMPOSITE PRODUCT FIX: Also index by the variant's raw Shopify product_id
          // so condition groups (which use raw Shopify IDs) can find this selection
          if (selection.variantId && variantObjectMap) {
            const numericVarId = parseInt(String(selection.variantId), 10);
            const variantObj =
              variantObjectMap.get(numericVarId) ||
              variantObjectMap.get(String(selection.variantId));
            if (variantObj) {
              // source_product_id comes from composite clustering (Layer 2)
              // product_id comes from the variant's original Shopify data
              const rawProductId =
                variantObj.source_product_id || variantObj.product_id;
              if (
                rawProductId &&
                String(rawProductId) !== String(selection.productId)
              ) {
                appliedProductSelections.set(String(rawProductId), selection);
              }
            }
          }
        }
      });

      return appliedProductSelections;
    },

    /**
     * Resolve the concrete target of an AUTO_APPLY_OPTION rule: the best
     * material-matching target variant plus its PPD category and product.
     * Shared by the classic auto-apply evaluator and the ENFORCED_PAIR evaluator
     * so both resolve targets identically.
     *
     * PATTERN 7: Perfect Dictionary Only (PPD-First via variantObjectMap)
     *
     * @param {Object} rule - Unified AUTO_APPLY_OPTION rule
     * @param {Object|null} sourceVariant - PPD variant object that triggered the rule
     * @param {Map|Object} appliedSelections - Current applied selections
     * @param {Map} variantObjectMap - PPD variantIdToVariantObjectMap
     * @returns {{targetVariantId: string, targetCategoryId: (string|null), targetProductId: (string|null)}|null}
     */
    _resolveAutoApplyTarget(
      rule,
      sourceVariant,
      appliedSelections,
      variantObjectMap
    ) {
      // Find the best material-matching target variant
      // Use effective targets (merges targetVariants + targetConditionGroups)
      const targetVariantId = this._findMaterialMatchingTarget(
        sourceVariant,
        this._getEffectiveTargetVariants(rule),
        variantObjectMap
      );

      if (!targetVariantId) {
        isDev() &&
          console.warn(
            '[ForbiddenValidator] AUTO_APPLY: No matching target variant found for rule "' +
              rule.name +
              '"'
          );
        return null;
      }

      // Resolve the target variant's categoryId and productId via its PPD entry
      const numericTargetId = parseInt(targetVariantId, 10);
      const targetVariant =
        variantObjectMap.get(numericTargetId) ||
        variantObjectMap.get(targetVariantId);
      // CRITICAL FIX: Use productId (composite ID) from PPD, falling back to source_product_id (raw Shopify ID).
      // The PPD variant object does NOT have product_id, it has productId (composite) and source_product_id (raw).
      const targetProductId = targetVariant
        ? String(
            targetVariant.productId || targetVariant.source_product_id || ''
          )
        : null;

      // CRITICAL FIX: Resolve categoryId directly from the PPD variant object's category field,
      // which is set during Layer 4 PPD building. This avoids mismatches between
      // composite product IDs and raw Shopify product IDs in applied selections lookups.
      let targetCategoryId = targetVariant?.category || null;

      // Fallback 1: Check applied selections for a matching product
      if (!targetCategoryId && targetProductId) {
        iterateAppliedSelections(appliedSelections, (catId, sel) => {
          if (String(sel?.productId) === targetProductId) {
            targetCategoryId = catId;
          }
        });
        if (targetCategoryId) {
          isDev() &&
            console.warn(
              '[ForbiddenValidator] AUTO_APPLY: Fallback 1 used for targetCategoryId resolution ' +
                '(applied selections lookup). This may indicate a PPD data integrity issue - ' +
                'variant.category was missing from PPD entry.',
              {
                targetVariantId: String(targetVariantId),
                targetProductId: targetProductId,
                resolvedCategoryId: targetCategoryId,
                ruleName: rule.name,
              }
            );
        }
      }

      // Fallback 2: Try to resolve via PPD variantIdToProductMap
      if (
        !targetCategoryId &&
        window.MurphyDoorConfigurator?.conductor?.getProductByVariantId
      ) {
        const product =
          window.MurphyDoorConfigurator.conductor.getProductByVariantId(
            targetVariantId
          );
        targetCategoryId =
          product?.category ||
          product?.categoryId ||
          product?.category_id ||
          null;
        if (targetCategoryId) {
          isDev() &&
            console.warn(
              '[ForbiddenValidator] AUTO_APPLY: Fallback 2 used for targetCategoryId resolution ' +
                '(conductor.getProductByVariantId lookup). This may indicate a PPD data integrity issue - ' +
                'variant.category was missing from PPD entry and applied selections lookup also failed.',
              {
                targetVariantId: String(targetVariantId),
                resolvedCategoryId: targetCategoryId,
                ruleName: rule.name,
              }
            );
        }
      }

      return {
        targetVariantId: String(targetVariantId),
        targetCategoryId: targetCategoryId,
        targetProductId: targetProductId,
      };
    },

    /**
     * True when a rule opted into ENFORCED_PAIR semantics. Only AUTO_APPLY_OPTION
     * rules can be enforced pairs; ENFORCED_PAIR on any other target action is
     * ignored here (the preemptive DISABLE pass treats unknown directions as
     * BIDIRECTIONAL, which is its long-standing safe default).
     *
     * @param {Object} rule - Unified rule
     * @returns {boolean}
     */
    _isEnforcedPairRule(rule) {
      const FR = CONSTANTS?.FORBIDDEN_RULES;
      if (!rule || !FR) return false;
      return (
        rule.targetAction === FR.TARGET_ACTIONS.AUTO_APPLY_OPTION &&
        rule.conflict_direction === FR.CONFLICT_DIRECTIONS.ENFORCED_PAIR
      );
    },

    /**
     * Locate the applied selection that satisfies an enforced rule's condition.
     * Variant-list groups are matched first; product-level groups (no variant
     * list) fall back to the group's check_product selection.
     *
     * @returns {{categoryId: (string|null), variantId: (string|null)}}
     */
    _findConditionHolder(
      rule,
      conditionVariantIds,
      appliedSelections,
      appliedProductSelections
    ) {
      let categoryId = null;
      let variantId = null;
      iterateAppliedSelections(appliedSelections, (catId, sel) => {
        if (categoryId || !sel?.variantId) return;
        if (conditionVariantIds.has(String(sel.variantId))) {
          categoryId = catId;
          variantId = String(sel.variantId);
        }
      });
      if (!categoryId) {
        (rule.conditionGroups || []).some((group) => {
          const sel = group?.productId
            ? appliedProductSelections.get(String(group.productId))
            : null;
          if (!sel?.variantId) return false;
          iterateAppliedSelections(appliedSelections, (catId, s) => {
            if (s === sel) categoryId = catId;
          });
          variantId = String(sel.variantId);
          return !!categoryId;
        });
      }
      return {
        categoryId: categoryId,
        variantId: categoryId ? variantId : null,
      };
    },

    /**
     * ENFORCED_PAIR state audit (pure). Returns every enforced rule whose
     * condition is applied while its target category holds a NON-target
     * variant. Used as the add-to-cart guard: it catches mismatches that
     * arrived through paths that never run the per-apply evaluator (?config=
     * restore, implicit/material cascades, anything auto_applied), without
     * rewriting those selections.
     *
     * An EMPTY target category is not a violation here: a required empty
     * category already blocks Add to Cart through the progress tracker.
     *
     * @param {Map|Object} appliedSelections - Current applied selections
     * @param {Object} forbiddenRules - { unifiedRules: Map, autoApplyRules?: Map }
     * @param {Map} variantObjectMap - PPD variantIdToVariantObjectMap
     * @returns {Array<{ruleId, ruleName, ruleDescription, conditionCategoryId, conditionVariantId, targetCategoryId, offendingVariantId}>}
     */
    findEnforcedPairViolations(
      appliedSelections,
      forbiddenRules,
      variantObjectMap
    ) {
      const violations = [];
      try {
        if (
          !appliedSelections ||
          !forbiddenRules?.unifiedRules ||
          !(forbiddenRules.unifiedRules instanceof Map) ||
          !(variantObjectMap instanceof Map)
        ) {
          return violations;
        }
        const rulesToCheck =
          forbiddenRules.autoApplyRules instanceof Map
            ? forbiddenRules.autoApplyRules
            : forbiddenRules.unifiedRules;
        const enforcedRules = [];
        rulesToCheck.forEach((rule, ruleId) => {
          if (this._isEnforcedPairRule(rule))
            enforcedRules.push([ruleId, rule]);
        });
        if (enforcedRules.length === 0) return violations;

        const lookupVariant = (id) =>
          variantObjectMap.get(parseInt(String(id), 10)) ||
          variantObjectMap.get(String(id)) ||
          null;
        const appliedProductSelections = this._buildAppliedProductSelections(
          appliedSelections,
          variantObjectMap
        );

        enforcedRules.forEach(([ruleId, rule]) => {
          try {
            if (
              !this._checkUnifiedRule(
                rule,
                appliedSelections,
                appliedProductSelections
              ).hasConflict
            ) {
              return;
            }
            const conditionVariantIds = this._getVariantIdsFromConditionGroups(
              rule.conditionGroups,
              true
            );
            const targetVariantIds = this._getEffectiveTargetVariants(rule);
            const targetCategoryIds = new Set();
            targetVariantIds.forEach((vid) => {
              const cat = lookupVariant(vid)?.category;
              if (cat) targetCategoryIds.add(cat);
            });
            const holder = this._findConditionHolder(
              rule,
              conditionVariantIds,
              appliedSelections,
              appliedProductSelections
            );

            iterateAppliedSelections(appliedSelections, (catId, sel) => {
              if (
                !sel?.variantId ||
                !targetCategoryIds.has(catId) ||
                catId === holder.categoryId ||
                targetVariantIds.has(String(sel.variantId))
              ) {
                return;
              }
              violations.push({
                ruleId: ruleId,
                ruleName: rule.name,
                ruleDescription: rule.description || '',
                conditionCategoryId: holder.categoryId,
                conditionVariantId: holder.variantId,
                targetCategoryId: catId,
                offendingVariantId: String(sel.variantId),
              });
            });
          } catch (ruleError) {
            isDev() &&
              console.error(
                '[ForbiddenValidator] ENFORCED_PAIR audit: rule "' +
                  (rule?.name || ruleId) +
                  '" failed:',
                ruleError
              );
          }
        });
        return violations;
      } catch (error) {
        isDev() &&
          console.error(
            '[ForbiddenValidator] findEnforcedPairViolations failed:',
            error
          );
        return violations;
      }
    },

    /**
     * ENFORCED_PAIR evaluator (pure: reads its arguments, mutates nothing,
     * publishes nothing on the success path).
     *
     * Called for every CUSTOMER-applied selection (never for auto_applied ones,
     * the conductor guards that). For each AUTO_APPLY_OPTION rule whose
     * conflict_direction is ENFORCED_PAIR it returns zero or more actions:
     *
     *   FORCE_APPLY    - the applied variant is one of the rule's condition
     *                    variants and the conditions are satisfied: apply the
     *                    target even over a manual pick. Omitted when a target
     *                    variant is already the applied selection there.
     *   CLEAR_CATEGORY - the applied variant sits in the rule's TARGET category
     *                    but is not a target variant, while the rule's condition
     *                    is still satisfied by another category's selection:
     *                    clear that condition category so the customer re-picks.
     *
     * Conflict resolution (one event can never both force and clear the same
     * category): a CLEAR never targets the category just applied, never targets
     * a category a FORCE_APPLY in the same result writes to, and each category is
     * cleared at most once.
     *
     * @param {string} appliedVariantId - Variant the customer just applied
     * @param {string} appliedCategoryId - Category it was applied in
     * @param {Map|Object} appliedSelections - Applied selections AFTER the apply
     * @param {Object} forbiddenRules - { unifiedRules: Map, autoApplyRules?: Map }
     * @param {Map} variantObjectMap - PPD variantIdToVariantObjectMap
     * @returns {Array<Object>} actions (see above), empty when nothing to do
     */
    evaluateEnforcedPairActions(
      appliedVariantId,
      appliedCategoryId,
      appliedSelections,
      forbiddenRules,
      variantObjectMap
    ) {
      const startTime = performance.now();
      const ACTIONS = CONSTANTS?.FORBIDDEN_RULES?.ENFORCED_PAIR_ACTIONS;
      const forceActions = [];
      const clearActions = [];

      try {
        if (
          !ACTIONS ||
          !appliedVariantId ||
          !forbiddenRules?.unifiedRules ||
          !(forbiddenRules.unifiedRules instanceof Map) ||
          !(variantObjectMap instanceof Map)
        ) {
          return [];
        }

        const rulesToCheck =
          forbiddenRules.autoApplyRules instanceof Map
            ? forbiddenRules.autoApplyRules
            : forbiddenRules.unifiedRules;

        // Cheap pre-check: most stores have no enforced pairs at all.
        const enforcedRules = [];
        rulesToCheck.forEach((rule, ruleId) => {
          if (this._isEnforcedPairRule(rule)) {
            enforcedRules.push([ruleId, rule]);
          }
        });
        if (enforcedRules.length === 0) {
          return [];
        }

        const lookupVariant = (id) =>
          variantObjectMap.get(parseInt(String(id), 10)) ||
          variantObjectMap.get(String(id)) ||
          null;

        const appliedVariantIdStr = String(appliedVariantId);
        const sourceVariant = lookupVariant(appliedVariantIdStr);
        const appliedCategory =
          appliedCategoryId || sourceVariant?.category || null;

        const appliedProductSelections = this._buildAppliedProductSelections(
          appliedSelections,
          variantObjectMap
        );

        enforcedRules.forEach(([ruleId, rule]) => {
          try {
            const conditionVariantIds = this._getVariantIdsFromConditionGroups(
              rule.conditionGroups,
              true
            );
            const targetVariantIds = this._getEffectiveTargetVariants(rule);

            // Categories that hold this rule's targets (PPD category field)
            const targetCategoryIds = new Set();
            targetVariantIds.forEach((vid) => {
              const cat = lookupVariant(vid)?.category;
              if (cat) targetCategoryIds.add(cat);
            });

            const conditionResult = this._checkUnifiedRule(
              rule,
              appliedSelections,
              appliedProductSelections
            );
            if (!conditionResult.hasConflict) {
              // "hasConflict" == conditions satisfied (shared semantics with
              // evaluateAutoApplyRulesOnApply). Unsatisfied: nothing to enforce.
              return;
            }

            // ── FORWARD: applied variant is a condition of this rule ──────────
            if (conditionVariantIds.has(appliedVariantIdStr)) {
              // Skip when a target variant is already applied in its category
              let targetAlreadyApplied = false;
              iterateAppliedSelections(appliedSelections, (catId, sel) => {
                if (
                  sel?.variantId &&
                  targetVariantIds.has(String(sel.variantId)) &&
                  (targetCategoryIds.size === 0 || targetCategoryIds.has(catId))
                ) {
                  targetAlreadyApplied = true;
                }
              });
              if (targetAlreadyApplied) return;

              const resolved = this._resolveAutoApplyTarget(
                rule,
                sourceVariant,
                appliedSelections,
                variantObjectMap
              );
              if (!resolved || !resolved.targetCategoryId) return;
              // Never force into the category the customer just picked in
              if (resolved.targetCategoryId === appliedCategory) return;

              forceActions.push({
                type: ACTIONS.FORCE_APPLY,
                ruleId: ruleId,
                ruleName: rule.name,
                ruleDescription: rule.description || '',
                conditionVariantId: appliedVariantIdStr,
                conditionCategoryId: appliedCategory,
                targetVariantId: resolved.targetVariantId,
                targetCategoryId: resolved.targetCategoryId,
                targetProductId: resolved.targetProductId,
                priority: rule.priority,
              });
              return;
            }

            // ── REVERSE: non-target pick inside the rule's target category ──
            if (
              appliedCategory &&
              targetCategoryIds.has(appliedCategory) &&
              !targetVariantIds.has(appliedVariantIdStr)
            ) {
              // Which category holds the satisfying condition selection?
              const conditionHolder = this._findConditionHolder(
                rule,
                conditionVariantIds,
                appliedSelections,
                appliedProductSelections
              );
              const conditionCategoryId = conditionHolder.categoryId;
              const satisfyingVariantId = conditionHolder.variantId;

              if (
                !conditionCategoryId ||
                conditionCategoryId === appliedCategory
              ) {
                return;
              }

              clearActions.push({
                type: ACTIONS.CLEAR_CATEGORY,
                ruleId: ruleId,
                ruleName: rule.name,
                ruleDescription: rule.description || '',
                clearCategoryId: conditionCategoryId,
                clearedVariantId: satisfyingVariantId,
                triggerCategoryId: appliedCategory,
                triggerVariantId: appliedVariantIdStr,
                priority: rule.priority,
              });
            }
          } catch (ruleError) {
            isDev() &&
              console.error(
                '[ForbiddenValidator] ENFORCED_PAIR: Error processing rule "' +
                  (rule?.name || ruleId) +
                  '":',
                ruleError
              );
          }
        });

        // Conflict resolution: FORCE wins; one clear per category.
        const forcedCategories = new Set(
          forceActions.map((a) => a.targetCategoryId)
        );
        const clearedCategories = new Set();
        const resolvedClears = clearActions.filter((a) => {
          if (
            a.clearCategoryId === appliedCategory ||
            forcedCategories.has(a.clearCategoryId) ||
            clearedCategories.has(a.clearCategoryId)
          ) {
            return false;
          }
          clearedCategories.add(a.clearCategoryId);
          return true;
        });

        const executionTime = performance.now() - startTime;
        if (executionTime > FORBIDDEN_CONSTANTS.PERFORMANCE_THRESHOLD) {
          publishEvent(CONSTANTS.EVENTS.PERFORMANCE_AUTO_APPLY_SLOW, {
            module: 'forbidden-validator',
            method: 'evaluateEnforcedPairActions',
            executionTime: executionTime,
            threshold: FORBIDDEN_CONSTANTS.PERFORMANCE_THRESHOLD,
            timestamp: Date.now(),
          });
        }

        return forceActions.concat(resolvedClears);
      } catch (error) {
        isDev() &&
          console.error(
            '[ForbiddenValidator] evaluateEnforcedPairActions failed:',
            error
          );
        publishEvent(CONSTANTS.EVENTS.FORBIDDEN_AUTO_APPLY_FAILED, {
          error: error.message,
          appliedVariantId: String(appliedVariantId),
          source: 'ForbiddenValidator.evaluateEnforcedPairActions',
          timestamp: Date.now(),
        });
        return [];
      }
    },

    /**
     * Find the best material-matching target variant from a set of target variants.
     * Priority: exact match (wood_sku + finish_sku) > wood_sku only > finish_sku only > first available.
     *
     * PATTERN 7: Perfect Dictionary Only (PPD-First via variantObjectMap)
     *
     * @param {Object|null} sourceVariant - The source variant object with wood_sku/finish_sku
     * @param {Set} targetVariantSet - Set of target variant IDs (strings)
     * @param {Map} variantObjectMap - PPD's variantIdToVariantObjectMap
     * @returns {string|null} Best matching target variant ID, or null if none found
     */
    _findMaterialMatchingTarget(
      sourceVariant,
      targetVariantSet,
      variantObjectMap
    ) {
      if (!targetVariantSet || targetVariantSet.size === 0) {
        return null;
      }

      const targetIds = Array.from(targetVariantSet);

      // If no source variant or no material info, return first available target
      if (
        !sourceVariant ||
        (!sourceVariant.wood_sku && !sourceVariant.finish_sku)
      ) {
        return targetIds[0] || null;
      }

      const sourceWood = sourceVariant.wood_sku || null;
      const sourceFinish = sourceVariant.finish_sku || null;

      let exactMatch = null;
      let woodOnlyMatch = null;
      let finishOnlyMatch = null;
      let firstAvailable = null;

      for (let i = 0; i < targetIds.length; i++) {
        const targetId = targetIds[i];
        const numericTargetId = parseInt(targetId, 10);
        const targetVariant =
          variantObjectMap.get(numericTargetId) ||
          variantObjectMap.get(targetId);

        if (!targetVariant) {
          continue;
        }

        if (!firstAvailable) {
          firstAvailable = targetId;
        }

        var targetWood = targetVariant.wood_sku || null;
        var targetFinish = targetVariant.finish_sku || null;

        // Priority 1: Exact match (both wood and finish)
        if (
          sourceWood &&
          sourceFinish &&
          targetWood === sourceWood &&
          targetFinish === sourceFinish
        ) {
          exactMatch = targetId;
          break; // Best possible match, stop searching
        }

        // Priority 2: Wood SKU match only
        if (sourceWood && targetWood === sourceWood && !woodOnlyMatch) {
          woodOnlyMatch = targetId;
        }

        // Priority 3: Finish SKU match only
        if (sourceFinish && targetFinish === sourceFinish && !finishOnlyMatch) {
          finishOnlyMatch = targetId;
        }
      }

      var bestMatch =
        exactMatch || woodOnlyMatch || finishOnlyMatch || firstAvailable;

      isDev() &&
        console.log('[ForbiddenValidator] _findMaterialMatchingTarget result', {
          sourceWood: sourceWood,
          sourceFinish: sourceFinish,
          matchType: exactMatch
            ? 'exact'
            : woodOnlyMatch
              ? 'wood_only'
              : finishOnlyMatch
                ? 'finish_only'
                : 'first_available',
          selectedTarget: bestMatch,
          candidateCount: targetIds.length,
        });

      return bestMatch;
    },

    /**
     * Evaluate AUTO_APPLY_OPTION rules when a selection is CLEARED/REMOVED.
     * Determines which auto-applied targets should be un-applied because
     * their triggering conditions are no longer satisfied.
     *
     * PATTERN 14: Try/Catch + Failed Events
     *
     * @param {string} clearedCategoryId - The category whose selection was just cleared
     * @param {Map} activeAutoApplyRules - Map of ruleId -> { conditionVariantId, targetVariantId, targetCategoryId }
     * @param {Map|Object} currentAppliedSelections - Current applied selections AFTER the clear
     * @param {Object} forbiddenRules - Unified forbidden rules { unifiedRules: Map }
     * @returns {Array} Array of { ruleId, targetCategoryId, targetVariantId } to un-apply
     */
    evaluateAutoApplyRulesOnClear(
      clearedCategoryId,
      activeAutoApplyRules,
      currentAppliedSelections,
      forbiddenRules
    ) {
      const startTime = performance.now();
      const rulesToUnapply = [];

      try {
        if (!activeAutoApplyRules || activeAutoApplyRules.size === 0) {
          return rulesToUnapply;
        }

        if (
          !forbiddenRules?.unifiedRules ||
          !(forbiddenRules.unifiedRules instanceof Map)
        ) {
          return rulesToUnapply;
        }

        // Build O(1) lookup for productId -> selection
        const appliedProductSelections = new Map();
        iterateAppliedSelections(
          currentAppliedSelections,
          (categoryId, selection) => {
            if (selection?.productId) {
              appliedProductSelections.set(
                String(selection.productId),
                selection
              );
            }
          }
        );

        activeAutoApplyRules.forEach((ruleState, ruleId) => {
          try {
            var rule = forbiddenRules.unifiedRules.get(ruleId);
            if (!rule) {
              // Rule no longer exists - should be cleaned up
              rulesToUnapply.push({
                ruleId: ruleId,
                targetCategoryId: ruleState.targetCategoryId,
                targetVariantId: ruleState.targetVariantId,
                reason: 'rule_no_longer_exists',
              });
              return;
            }

            // Re-evaluate the rule conditions against current selections (AFTER clear)
            var conditionResult = this._checkUnifiedRule(
              rule,
              currentAppliedSelections,
              appliedProductSelections
            );

            // If conditions are NO LONGER satisfied, the auto-applied target should be removed
            if (!conditionResult.hasConflict) {
              rulesToUnapply.push({
                ruleId: ruleId,
                ruleName: rule.name,
                targetCategoryId: ruleState.targetCategoryId,
                targetVariantId: ruleState.targetVariantId,
                conditionVariantId: ruleState.conditionVariantId,
                reason: 'conditions_no_longer_satisfied',
              });

              isDev() &&
                console.log(
                  '[ForbiddenValidator] AUTO_UNAPPLY: Rule "' +
                    rule.name +
                    '" conditions no longer met',
                  {
                    clearedCategoryId: clearedCategoryId,
                    targetCategoryId: ruleState.targetCategoryId,
                    targetVariantId: ruleState.targetVariantId,
                  }
                );
            }
          } catch (ruleError) {
            isDev() &&
              console.error(
                '[ForbiddenValidator] AUTO_UNAPPLY: Error evaluating rule "' +
                  ruleId +
                  '":',
                ruleError
              );
          }
        });

        var executionTime = performance.now() - startTime;

        isDev() &&
          console.log('[ForbiddenValidator] AUTO_UNAPPLY evaluation complete', {
            clearedCategoryId: clearedCategoryId,
            activeRulesChecked: activeAutoApplyRules.size,
            rulesToUnapply: rulesToUnapply.length,
            executionTime: executionTime.toFixed(2) + 'ms',
          });

        return rulesToUnapply;
      } catch (error) {
        isDev() &&
          console.error(
            '[ForbiddenValidator] evaluateAutoApplyRulesOnClear failed:',
            error
          );
        publishEvent(CONSTANTS.EVENTS.FORBIDDEN_AUTO_UNAPPLY_FAILED, {
          error: error.message,
          clearedCategoryId: clearedCategoryId,
          source: 'ForbiddenValidator.evaluateAutoApplyRulesOnClear',
          timestamp: Date.now(),
        });
        return rulesToUnapply;
      }
    },

    /**
     * PREEMPTIVE FORBIDDEN CALCULATION: Calculate which items should be DISABLED
     * across ALL categories based on current applied selections.
     *
     * This enables PREEMPTIVE blocking - disable options BEFORE user can select them.
     * Supports bidirectional rules (default), one-way rules, and mutual exclusion.
     *
     * @param {Map|Object} appliedSelections - Current applied selections
     * @param {Object} forbiddenRules - Unified forbidden rules { unifiedRules: Map }
     * @returns {Map} variantId -> [{reason, ruleId, ruleName, category, priority}]
     */
    calculatePreemptiveForbiddenItems(appliedSelections, forbiddenRules) {
      const startTime = performance.now();
      const itemsToDisable = new Map();

      try {
        // Validate inputs
        if (
          !appliedSelections ||
          getAppliedSelectionsCount(appliedSelections) === 0
        ) {
          isDev() &&
            console.log(
              '🛡️ PREEMPTIVE: No applied selections - nothing to preemptively disable'
            );
          return itemsToDisable;
        }

        if (
          !forbiddenRules ||
          !forbiddenRules.unifiedRules ||
          !(forbiddenRules.unifiedRules instanceof Map)
        ) {
          isDev() &&
            console.warn(
              '⚠️ PREEMPTIVE: No unified rules available for preemptive calculation'
            );
          return itemsToDisable;
        }

        if (forbiddenRules.unifiedRules.size === 0) {
          isDev() &&
            console.log(
              '🛡️ PREEMPTIVE: No unified rules configured - no preemptive disabling needed'
            );
          return itemsToDisable;
        }

        // PERFORMANCE OPTIMIZATION: Create lookup structures once for O(1) lookups
        // Instead of iterating appliedSelections repeatedly in nested loops

        // Set of applied variant IDs for quick "is this variant applied?" checks
        const appliedVariantIdsSet = new Set();

        // Map of productId → selection for quick "what variant is selected for this product?" lookups
        const appliedProductSelections = new Map();

        iterateAppliedSelections(appliedSelections, (categoryId, selection) => {
          if (selection?.variantId) {
            appliedVariantIdsSet.add(String(selection.variantId));
          }
          if (selection?.productId) {
            appliedProductSelections.set(
              String(selection.productId),
              selection
            );
          }
        });

        // Publish preemptive calculation started event
        publishEvent(CONSTANTS.EVENTS.DATA_VALIDATION_STARTED, {
          type: 'preemptive_calculation',
          appliedSelectionsCount: getAppliedSelectionsCount(appliedSelections),
          ruleCount: forbiddenRules.unifiedRules.size,
          timestamp: Date.now(),
          source: 'ForbiddenValidator.calculatePreemptiveForbiddenItems',
        });

        let rulesProcessed = 0;
        let rulesWithMissingDirection = 0;

        // Process each unified rule based on its conflict_direction
        // PERFORMANCE: Use pre-partitioned conflictRules map when available (excludes AUTO_APPLY_OPTION)
        const preemptiveRulesToCheck =
          forbiddenRules.conflictRules || forbiddenRules.unifiedRules;
        preemptiveRulesToCheck.forEach((rule, ruleId) => {
          try {
            // Determine conflict direction - DEFAULT to BIDIRECTIONAL per user requirement
            const conflictDirection =
              rule.conflict_direction || 'BIDIRECTIONAL';

            // Log warning for rules missing the field (helps user know which to update)
            if (!rule.conflict_direction) {
              rulesWithMissingDirection++;
              isDev() &&
                console.warn(
                  `⚠️ PREEMPTIVE: Rule "${rule.name}" (id: ${ruleId}) missing conflict_direction, defaulting to BIDIRECTIONAL`
                );
            }

            switch (conflictDirection) {
              case 'ONE_WAY':
                this._processOneWayPreemptiveRule(
                  rule,
                  appliedSelections,
                  itemsToDisable,
                  appliedVariantIdsSet,
                  appliedProductSelections
                );
                break;

              case 'BIDIRECTIONAL':
                this._processBidirectionalPreemptiveRule(
                  rule,
                  appliedSelections,
                  itemsToDisable,
                  appliedVariantIdsSet,
                  appliedProductSelections
                );
                break;

              case 'MUTUAL_EXCLUSION':
                this._processMutualExclusionPreemptiveRule(
                  rule,
                  appliedSelections,
                  itemsToDisable,
                  appliedVariantIdsSet,
                  appliedProductSelections
                );
                break;

              default:
                isDev() &&
                  console.warn(
                    `⚠️ PREEMPTIVE: Unknown conflict_direction "${conflictDirection}" for rule "${rule.name}", defaulting to BIDIRECTIONAL`
                  );
                this._processBidirectionalPreemptiveRule(
                  rule,
                  appliedSelections,
                  itemsToDisable,
                  appliedVariantIdsSet,
                  appliedProductSelections
                );
                break;
            }

            rulesProcessed++;
          } catch (ruleError) {
            isDev() &&
              console.error(
                `❌ PREEMPTIVE: Error processing rule "${rule.name || ruleId}":`,
                ruleError
              );
          }
        });

        const executionTime = performance.now() - startTime;

        // ✅ CCP COMPLIANT: Use specific forbidden performance event (Pattern #13)
        if (executionTime > FORBIDDEN_CONSTANTS.PERFORMANCE_THRESHOLD) {
          publishEvent(CONSTANTS.EVENTS.PERFORMANCE_FORBIDDEN_VALIDATION_SLOW, {
            module: 'forbidden-validator',
            method: 'calculatePreemptiveForbiddenItems',
            executionTime,
            threshold: FORBIDDEN_CONSTANTS.PERFORMANCE_THRESHOLD,
            rulesProcessed,
            itemsDisabled: itemsToDisable.size,
            timestamp: Date.now(),
          });
        }

        // Publish completion event
        publishEvent(CONSTANTS.EVENTS.DATA_VALIDATION_COMPLETED, {
          type: 'preemptive_calculation',
          rulesProcessed,
          rulesWithMissingDirection,
          itemsToDisableCount: itemsToDisable.size,
          executionTime,
          timestamp: Date.now(),
          source: 'ForbiddenValidator.calculatePreemptiveForbiddenItems',
        });

        // Merge sold-out items — availability is selection-independent, so always included.
        // Sold-out entries prepend to any existing rule entries so they surface first in tooltips.
        if (_soldOutEntries.size > 0) {
          _soldOutEntries.forEach((entries, variantId) => {
            if (!itemsToDisable.has(variantId)) {
              itemsToDisable.set(variantId, [...entries]);
            } else {
              itemsToDisable.set(variantId, [
                ...entries,
                ...itemsToDisable.get(variantId),
              ]);
            }
          });
          isDev() &&
            console.log(
              `🛡️ PREEMPTIVE: Merged ${_soldOutEntries.size} sold-out item(s) into disable map`
            );
        }

        // PREEMPTIVE FORBIDDEN STATE: Update the in-memory state for click/apply blocking.
        // This ensures forbidden status is queryable even when DOM elements don't exist yet.
        _currentPreemptiveForbiddenState.clear();
        for (const [variantId, reasons] of itemsToDisable) {
          _currentPreemptiveForbiddenState.set(variantId, reasons);
        }

        isDev() &&
          console.log('🛡️ PREEMPTIVE: Calculation complete', {
            rulesProcessed,
            rulesWithMissingDirection,
            itemsToDisableCount: itemsToDisable.size,
            disabledVariants: Array.from(itemsToDisable.keys()),
            executionTime: `${executionTime.toFixed(2)}ms`,
          });

        return itemsToDisable;
      } catch (error) {
        const failedExecutionTime = performance.now() - startTime;
        isDev() &&
          console.error('❌ PREEMPTIVE: Calculation failed', {
            error: error.message,
            stack: error.stack,
            executionTime: `${failedExecutionTime.toFixed(2)}ms`,
          });

        // Publish failed event
        publishEvent(CONSTANTS.EVENTS.DATA_VALIDATION_FAILED, {
          type: 'preemptive_calculation',
          error: error.message,
          executionTime: failedExecutionTime,
          timestamp: Date.now(),
          source: 'ForbiddenValidator.calculatePreemptiveForbiddenItems',
        });

        return itemsToDisable; // Return empty map on failure (safe fallback)
      }
    },

    /**
     * PREEMPTIVE FORBIDDEN CHECK: Check if a variant is currently forbidden-disabled.
     * This is an O(1) in-memory lookup that works even when DOM elements don't exist.
     * Used by event-handler.js for click blocking and apply button blocking.
     *
     * @param {string} variantId - The variant ID to check
     * @returns {Object|null} Forbidden info {isForbidden, reasons} or null if not forbidden
     */
    isVariantPreemptivelyForbidden(variantId) {
      if (!variantId) return null;

      const variantIdStr = String(variantId);
      const reasons = _currentPreemptiveForbiddenState.get(variantIdStr);

      if (reasons && reasons.length > 0) {
        return {
          isForbidden: true,
          reasons,
          reasonText: reasons.map((r) => r.ruleName || r.reason).join('; '),
        };
      }

      return null;
    },

    /**
     * PREEMPTIVE FORBIDDEN CHECK: Get all currently forbidden variant IDs.
     * Used for debugging and comprehensive forbidden state queries.
     *
     * @returns {Map} Current preemptive forbidden state map
     */
    getCurrentPreemptiveForbiddenState() {
      return new Map(_currentPreemptiveForbiddenState);
    },
  };

  // 🚨 CCP COMPLIANCE: Expose module to global namespace properly
  // eslint-disable-next-line no-restricted-syntax
  window.MurphyDoorConfigurator.forbiddenValidator = forbiddenValidator;

  // Setup event listeners after validator is defined
  if (window.MurphyDoorConfigurator?.CoreEventBus) {
    // Event listener for browsing state validation
    // ✅ GOLDEN STANDARD: Inline guard inside handler (no wrapper function)
    // PATTERN #15: Track subscription for cleanup
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.SELECTION_BROWSING,
        (eventData) => {
          if (!forbiddenBootstrapComplete) {
            pendingCalls.push({
              fn: window.MurphyDoorConfigurator.forbiddenValidator.handleBrowsingValidation.bind(
                window.MurphyDoorConfigurator.forbiddenValidator
              ),
              context: null,
              args: [eventData],
            });
            return;
          }
          window.MurphyDoorConfigurator.forbiddenValidator.handleBrowsingValidation(
            eventData
          );
        }
      )
    );

    // Subscribe to selection:applied events to validate conflicts after Apply button
    // AND trigger PREEMPTIVE forbidden calculation for all other categories
    // ✅ GOLDEN STANDARD: Inline guard inside handler (no wrapper function)
    // PATTERN #15: Track subscription for cleanup
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.SELECTION_APPLIED,
        (eventData) => {
          if (!forbiddenBootstrapComplete) {
            pendingCalls.push({
              fn: window.MurphyDoorConfigurator.forbiddenValidator.handleAppliedValidation.bind(
                window.MurphyDoorConfigurator.forbiddenValidator
              ),
              context: null,
              args: [eventData],
            });
            return;
          }
          isDev() &&
            console.log(
              '🚨 FORBIDDEN: Received selection:applied event - validating against current selections'
            );

          // Step 1: Existing post-application validation
          window.MurphyDoorConfigurator.forbiddenValidator.handleAppliedValidation(
            eventData
          );

          // Step 2: NEW - Trigger PREEMPTIVE forbidden calculation for all categories
          // This calculates which items should be DISABLED based on the new selection state
          try {
            const conductor = window.MurphyDoorConfigurator.conductor;
            if (!conductor) {
              isDev() &&
                console.warn(
                  '⚠️ PREEMPTIVE: Conductor not available for preemptive calculation'
                );
              return;
            }

            const appliedSelections = conductor.getAppliedSelectionsMap();
            const forbiddenRules = conductor.getUnifiedForbiddenRules();

            if (
              !forbiddenRules ||
              !forbiddenRules.unifiedRules ||
              forbiddenRules.unifiedRules.size === 0
            ) {
              isDev() &&
                console.log(
                  '🛡️ PREEMPTIVE: No unified rules - skipping preemptive calculation'
                );
              return;
            }

            isDev() &&
              console.log(
                '🛡️ PREEMPTIVE: Triggering preemptive forbidden calculation after selection:applied',
                {
                  categoryId: eventData.categoryId,
                  appliedSelectionsCount: appliedSelections.size,
                  rulesCount: forbiddenRules.unifiedRules.size,
                }
              );

            const itemsToDisable =
              window.MurphyDoorConfigurator.forbiddenValidator.calculatePreemptiveForbiddenItems(
                appliedSelections,
                forbiddenRules
              );

            // Publish preemptive disabled items to UI for immediate visual disabling
            publishEvent(CONSTANTS.EVENTS.UI_UPDATE_PREEMPTIVE_FORBIDDEN, {
              itemsToDisable: Array.from(itemsToDisable.entries()),
              appliedSelectionsCount: appliedSelections.size,
              rulesProcessed: forbiddenRules.unifiedRules.size,
              triggerCategoryId: eventData.categoryId,
              timestamp: Date.now(),
              source: 'preemptive-validation-after-applied',
            });

            isDev() &&
              console.log(
                '🛡️ PREEMPTIVE: Published UI_UPDATE_PREEMPTIVE_FORBIDDEN event',
                {
                  disabledItemsCount: itemsToDisable.size,
                  triggerCategory: eventData.categoryId,
                }
              );
          } catch (preemptiveError) {
            isDev() &&
              console.error(
                '❌ PREEMPTIVE: Failed to calculate preemptive forbidden items:',
                preemptiveError
              );

            // Route through centralized error handler
            window.MurphyDoorConfigurator.utils.handleSystemError({
              type: 'PreemptiveValidationError',
              error: preemptiveError,
              source: 'forbiddenValidator.selectionAppliedHandler.preemptive',
              recoverable: true,
              impact: 'low',
              details: { eventData: eventData },
            });
          }
        }
      )
    );

    // ✅ PREEMPTIVE: Recalculate when selections are cleared/removed
    // When a selection is removed, previously forbidden items may become available again
    const recalculatePreemptiveAfterClear = (eventData) => {
      if (!forbiddenBootstrapComplete) return;

      // Skip when source is conductor.user_unapply — the SELECTION_USER_UNAPPLY_COMPLETED
      // handler below will run an identical recalculation, avoiding a redundant double-trigger.
      // STATE_SELECTION_CLEARED from other sources (rush_delivery_auto_deselect,
      // forbidden_auto_unapply) still need this handler since they don't fire unapply_completed.
      if (eventData && eventData.source === 'conductor.user_unapply') return;

      try {
        const conductor = window.MurphyDoorConfigurator.conductor;
        if (!conductor) return;

        const appliedSelections = conductor.getAppliedSelectionsMap();
        const forbiddenRules = conductor.getUnifiedForbiddenRules();

        isDev() &&
          console.log(
            '🛡️ PREEMPTIVE: Recalculating after selection cleared/removed',
            {
              categoryId: eventData.categoryId || 'unknown',
              remainingSelectionsCount: appliedSelections.size,
            }
          );

        const itemsToDisable =
          window.MurphyDoorConfigurator.forbiddenValidator.calculatePreemptiveForbiddenItems(
            appliedSelections,
            forbiddenRules
          );

        publishEvent(CONSTANTS.EVENTS.UI_UPDATE_PREEMPTIVE_FORBIDDEN, {
          itemsToDisable: Array.from(itemsToDisable.entries()),
          appliedSelectionsCount: appliedSelections.size,
          rulesProcessed: forbiddenRules.unifiedRules
            ? forbiddenRules.unifiedRules.size
            : 0,
          triggerCategoryId: eventData.categoryId || 'clear-all',
          timestamp: Date.now(),
          source: 'preemptive-validation-after-clear',
        });
      } catch (error) {
        isDev() &&
          console.error(
            '❌ PREEMPTIVE: Recalculation after clear failed:',
            error
          );
      }
    };

    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.STATE_SELECTION_CLEARED,
        recalculatePreemptiveAfterClear
      )
    );
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.STATE_SELECTION_REMOVED,
        (eventData) => {
          // Skip when source is conductor.user_unapply — the SELECTION_USER_UNAPPLY_COMPLETED
          // handler runs an identical recalculation, avoiding a redundant double-trigger.
          if (eventData && eventData.source === 'conductor.user_unapply')
            return;
          recalculatePreemptiveAfterClear(eventData);
        }
      )
    );

    // ✅ PREEMPTIVE: Recalculate when a user unapply completes
    // When a selection is unapplied, the constraint set changes — combinations that were
    // forbidden may now be valid (or new forbidden states may emerge).
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.SELECTION_USER_UNAPPLY_COMPLETED,
        (eventData) => {
          if (!forbiddenBootstrapComplete) return;

          try {
            const conductor = window.MurphyDoorConfigurator.conductor;
            if (!conductor) return;

            if (!eventData || !eventData.categoryId) {
              isDev() &&
                console.warn(
                  '⚠️ PREEMPTIVE: SELECTION_USER_UNAPPLY_COMPLETED missing categoryId, skipping'
                );
              return;
            }

            const appliedSelections = conductor.getAppliedSelectionsMap();
            const forbiddenRules = conductor.getUnifiedForbiddenRules();

            isDev() &&
              console.log(
                '🛡️ PREEMPTIVE: Recalculating after selection unapply completed',
                {
                  categoryId: eventData.categoryId,
                  remainingSelectionsCount: appliedSelections.size,
                }
              );

            const itemsToDisable =
              window.MurphyDoorConfigurator.forbiddenValidator.calculatePreemptiveForbiddenItems(
                appliedSelections,
                forbiddenRules
              );

            publishEvent(CONSTANTS.EVENTS.UI_UPDATE_PREEMPTIVE_FORBIDDEN, {
              itemsToDisable: Array.from(itemsToDisable.entries()),
              appliedSelectionsCount: appliedSelections.size,
              rulesProcessed: forbiddenRules.unifiedRules
                ? forbiddenRules.unifiedRules.size
                : 0,
              triggerCategoryId: eventData.categoryId,
              timestamp: Date.now(),
              source: 'preemptive-validation-after-unapply',
            });
          } catch (error) {
            isDev() &&
              console.error(
                '❌ PREEMPTIVE: Recalculation after unapply failed:',
                error
              );
          }
        }
      )
    );

    // ✅ PREEMPTIVE: Recalculate when a user unapply FAILS
    // If the conductor's unapply throws, state was NOT changed — but the preemptive forbidden
    // cache may be stale if an optimistic update ran earlier. Republish a fresh calculation
    // so the UI reflects the actual (unchanged) selection state.
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.SELECTION_USER_UNAPPLY_FAILED,
        (eventData) => {
          if (!forbiddenBootstrapComplete) return;

          try {
            const conductor = window.MurphyDoorConfigurator.conductor;
            if (!conductor) return;

            if (!eventData || !eventData.categoryId) {
              isDev() &&
                console.warn(
                  '⚠️ PREEMPTIVE: SELECTION_USER_UNAPPLY_FAILED missing categoryId, skipping'
                );
              return;
            }

            const appliedSelections = conductor.getAppliedSelectionsMap();
            const forbiddenRules = conductor.getUnifiedForbiddenRules();

            isDev() &&
              console.log(
                '🛡️ PREEMPTIVE: Recalculating after selection unapply FAILED (restoring correct state)',
                {
                  categoryId: eventData.categoryId,
                  error: eventData.error || 'unknown',
                  remainingSelectionsCount: appliedSelections.size,
                }
              );

            const itemsToDisable =
              window.MurphyDoorConfigurator.forbiddenValidator.calculatePreemptiveForbiddenItems(
                appliedSelections,
                forbiddenRules
              );

            publishEvent(CONSTANTS.EVENTS.UI_UPDATE_PREEMPTIVE_FORBIDDEN, {
              itemsToDisable: Array.from(itemsToDisable.entries()),
              appliedSelectionsCount: appliedSelections.size,
              rulesProcessed: forbiddenRules.unifiedRules
                ? forbiddenRules.unifiedRules.size
                : 0,
              triggerCategoryId: eventData.categoryId,
              timestamp: Date.now(),
              source: 'preemptive-validation-after-unapply-failed',
            });
          } catch (error) {
            isDev() &&
              console.error(
                '❌ PREEMPTIVE: Recalculation after unapply failure failed:',
                error
              );
          }
        }
      )
    );

    // ✅ PREEMPTIVE: Recalculate when material preferences are recomputed from applied state
    // The forbidden constraint matrix is preference-context-dependent. When the active
    // wood/finish preference SKU switches, combinations that were forbidden with the old
    // SKU may now be valid with the new SKU and vice versa.
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.MATERIAL_PREFERENCES_RECOMPUTED_FROM_APPLIED,
        (eventData) => {
          if (!forbiddenBootstrapComplete) return;

          try {
            const conductor = window.MurphyDoorConfigurator.conductor;
            if (!conductor) return;

            if (!eventData) {
              isDev() &&
                console.warn(
                  '⚠️ PREEMPTIVE: MATERIAL_PREFERENCES_RECOMPUTED_FROM_APPLIED missing event data, skipping'
                );
              return;
            }

            const appliedSelections = conductor.getAppliedSelectionsMap();
            const forbiddenRules = conductor.getUnifiedForbiddenRules();

            isDev() &&
              console.log(
                '🛡️ PREEMPTIVE: Recalculating after material preferences recomputed',
                {
                  unappliedCategoryId:
                    eventData.unappliedCategoryId || 'unknown',
                  survivingWoodSku: eventData.survivingWoodSku || 'none',
                  survivingFinishSku: eventData.survivingFinishSku || 'none',
                  previousWoodSku: eventData.previousWoodSku || 'none',
                  previousFinishSku: eventData.previousFinishSku || 'none',
                  cascade: eventData.cascade || false,
                  remainingSelectionsCount: appliedSelections.size,
                }
              );

            const itemsToDisable =
              window.MurphyDoorConfigurator.forbiddenValidator.calculatePreemptiveForbiddenItems(
                appliedSelections,
                forbiddenRules
              );

            publishEvent(CONSTANTS.EVENTS.UI_UPDATE_PREEMPTIVE_FORBIDDEN, {
              itemsToDisable: Array.from(itemsToDisable.entries()),
              appliedSelectionsCount: appliedSelections.size,
              rulesProcessed: forbiddenRules.unifiedRules
                ? forbiddenRules.unifiedRules.size
                : 0,
              triggerCategoryId:
                eventData.unappliedCategoryId ||
                'material-preference-recompute',
              timestamp: Date.now(),
              source:
                'preemptive-validation-after-material-preference-recompute',
            });
          } catch (error) {
            isDev() &&
              console.error(
                '❌ PREEMPTIVE: Recalculation after material preference recompute failed:',
                error
              );
          }
        }
      )
    );

    // ✅ PREEMPTIVE: Re-apply forbidden state when a category is opened
    // DOM elements for that category may not have existed during initial calculation
    // PATTERN #15: Track subscription for cleanup
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_CATEGORY_OPENED,
        (eventData) => {
          if (!forbiddenBootstrapComplete) return;

          try {
            const conductor = window.MurphyDoorConfigurator.conductor;
            if (!conductor) return;

            const appliedSelections = conductor.getAppliedSelectionsMap();

            // If no selections yet, sold-out items still need disabling — publish them directly.
            if (!appliedSelections || appliedSelections.size === 0) {
              if (_soldOutEntries.size > 0) {
                publishEvent(CONSTANTS.EVENTS.UI_UPDATE_PREEMPTIVE_FORBIDDEN, {
                  itemsToDisable: Array.from(_soldOutEntries.entries()),
                  appliedSelectionsCount: 0,
                  rulesProcessed: 0,
                  triggerCategoryId: eventData.categoryId || 'category-opened',
                  timestamp: Date.now(),
                  source: 'sold-out-category-opened',
                });
              }
              return;
            }

            const forbiddenRules = conductor.getUnifiedForbiddenRules();
            if (
              !forbiddenRules ||
              !forbiddenRules.unifiedRules ||
              forbiddenRules.unifiedRules.size === 0
            ) {
              // No forbidden rules, but sold-out items still need disabling.
              if (_soldOutEntries.size > 0) {
                publishEvent(CONSTANTS.EVENTS.UI_UPDATE_PREEMPTIVE_FORBIDDEN, {
                  itemsToDisable: Array.from(_soldOutEntries.entries()),
                  appliedSelectionsCount: appliedSelections.size,
                  rulesProcessed: 0,
                  triggerCategoryId: eventData.categoryId || 'category-opened',
                  timestamp: Date.now(),
                  source: 'sold-out-no-rules-category-opened',
                });
              }
              return;
            }

            isDev() &&
              console.log(
                '🛡️ PREEMPTIVE: Category opened - re-applying preemptive forbidden state',
                {
                  categoryId: eventData.categoryId || 'unknown',
                }
              );

            const itemsToDisable =
              window.MurphyDoorConfigurator.forbiddenValidator.calculatePreemptiveForbiddenItems(
                appliedSelections,
                forbiddenRules
              );

            if (itemsToDisable.size > 0) {
              publishEvent(CONSTANTS.EVENTS.UI_UPDATE_PREEMPTIVE_FORBIDDEN, {
                itemsToDisable: Array.from(itemsToDisable.entries()),
                appliedSelectionsCount: appliedSelections.size,
                rulesProcessed: forbiddenRules.unifiedRules.size,
                triggerCategoryId: eventData.categoryId || 'category-opened',
                timestamp: Date.now(),
                source: 'preemptive-validation-category-opened',
              });
            }
          } catch (error) {
            isDev() &&
              console.error(
                '❌ PREEMPTIVE: Re-apply on category open failed:',
                error
              );
          }
        }
      )
    );

    // PREEMPTIVE FORBIDDEN TIMING FIX: Subscribe to UI_CATEGORY_CONTENT_RENDERED
    // This event fires AFTER showCategoryContent() / renderCategoryThumbnails()
    // has actually inserted the thumbnail DOM elements, fixing the timing gap where
    // UI_CATEGORY_OPENED fires before rAF renders the thumbnails (notFoundCount > 0).
    // PATTERN #15: Track subscription for cleanup
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_CATEGORY_CONTENT_RENDERED,
        (eventData) => {
          if (!forbiddenBootstrapComplete) return;

          try {
            const conductor = window.MurphyDoorConfigurator.conductor;
            if (!conductor) return;

            const appliedSelections = conductor.getAppliedSelectionsMap();

            // If no selections yet, sold-out items still need disabling — publish them directly.
            if (!appliedSelections || appliedSelections.size === 0) {
              publishEvent(CONSTANTS.EVENTS.UI_UPDATE_PREEMPTIVE_FORBIDDEN, {
                itemsToDisable: Array.from(_soldOutEntries.entries()),
                appliedSelectionsCount: 0,
                rulesProcessed: 0,
                triggerCategoryId: eventData.categoryId || 'content-rendered',
                timestamp: Date.now(),
                source: 'sold-out-content-rendered',
              });
              return;
            }

            const forbiddenRules = conductor.getUnifiedForbiddenRules();
            if (
              !forbiddenRules ||
              !forbiddenRules.unifiedRules ||
              forbiddenRules.unifiedRules.size === 0
            ) {
              // No forbidden rules — publish just sold-out items (possibly empty, to clear stale classes).
              publishEvent(CONSTANTS.EVENTS.UI_UPDATE_PREEMPTIVE_FORBIDDEN, {
                itemsToDisable: Array.from(_soldOutEntries.entries()),
                appliedSelectionsCount: appliedSelections.size,
                rulesProcessed: 0,
                triggerCategoryId: eventData.categoryId || 'content-rendered',
                timestamp: Date.now(),
                source: 'sold-out-no-rules-content-rendered',
              });
              return;
            }

            isDev() &&
              console.log(
                '🛡️ PREEMPTIVE: Content rendered - applying preemptive forbidden state to new DOM elements',
                {
                  categoryId: eventData.categoryId || 'unknown',
                  source: eventData.source || 'unknown',
                }
              );

            const itemsToDisable =
              window.MurphyDoorConfigurator.forbiddenValidator.calculatePreemptiveForbiddenItems(
                appliedSelections,
                forbiddenRules
              );

            // Always publish (even if empty) to clear stale forbidden-disabled classes
            publishEvent(CONSTANTS.EVENTS.UI_UPDATE_PREEMPTIVE_FORBIDDEN, {
              itemsToDisable: Array.from(itemsToDisable.entries()),
              appliedSelectionsCount: appliedSelections.size,
              rulesProcessed: forbiddenRules.unifiedRules.size,
              triggerCategoryId: eventData.categoryId || 'content-rendered',
              timestamp: Date.now(),
              source: 'preemptive-validation-content-rendered',
            });
          } catch (error) {
            isDev() &&
              console.error(
                '❌ PREEMPTIVE: Apply on content rendered failed:',
                error
              );
          }
        }
      )
    );

    // ✅ CCP COMPLIANCE: Subscribe to preemptive validation requests from conductor
    // ✅ GOLDEN STANDARD: Inline guard inside handler (no wrapper function)
    // PATTERN #15: Track subscription for cleanup
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.DATA_PREEMPTIVE_FORBIDDEN_REQUEST,
        (eventData) => {
          if (!forbiddenBootstrapComplete) {
            pendingCalls.push({
              fn: async function preemptiveValidation(data) {
                // Resubmit via event to self (this is queued, so re-dispatch after bootstrap)
                window.MurphyDoorConfigurator.CoreEventBus.publish(
                  CONSTANTS.EVENTS.DATA_PREEMPTIVE_FORBIDDEN_REQUEST,
                  data
                );
              },
              context: null,
              args: [eventData],
            });
            return;
          }

          (async () => {
            isDev() &&
              console.log(
                '🚀 FORBIDDEN: Received preemptive forbidden validation request',
                {
                  requestId: eventData.requestId,
                  categoryId: eventData.categoryId,
                  source: eventData.source,
                }
              );

            try {
              // ✅ CCP COMPLIANCE: Use the existing getPotentiallyForbiddenItems method
              const potentiallyForbiddenItems =
                await window.MurphyDoorConfigurator.forbiddenValidator.getPotentiallyForbiddenItems(
                  eventData.appliedSelections,
                  eventData.forbiddenRules,
                  eventData.categoryId
                );

              // ✅ CCP COMPLIANCE: Publish response event back to conductor
              publishEvent(
                CONSTANTS.EVENTS.DATA_PREEMPTIVE_FORBIDDEN_RESPONSE,
                {
                  requestId: eventData.requestId,
                  categoryId: eventData.categoryId,
                  forbiddenItems: potentiallyForbiddenItems,
                  success: true,
                  source: 'forbidden-validator.preemptive_validation',
                }
              );

              isDev() &&
                console.log(
                  '✅ FORBIDDEN: Preemptive validation response sent',
                  {
                    requestId: eventData.requestId,
                    categoryId: eventData.categoryId,
                    forbiddenItemsCount: potentiallyForbiddenItems?.size || 0,
                  }
                );
            } catch (error) {
              isDev() &&
                console.error('❌ FORBIDDEN: Preemptive validation failed', {
                  requestId: eventData.requestId,
                  categoryId: eventData.categoryId,
                  error: error.message,
                });

              // ✅ CCP COMPLIANCE: Publish error response
              publishEvent(
                CONSTANTS.EVENTS.DATA_PREEMPTIVE_FORBIDDEN_RESPONSE,
                {
                  requestId: eventData.requestId,
                  categoryId: eventData.categoryId,
                  forbiddenItems: new Set(),
                  success: false,
                  error: error.message,
                  source: 'forbidden-validator.preemptive_validation',
                }
              );
            }
          })();
        }
      )
    );
  }

  // ✅ GOLDEN STANDARD: Initialization triggered automatically by bootstrap queue
  // When system:state_from_ppd_ready fires, the module-load-time subscription above
  // processes all pendingCalls. The initialize() method is called as part of the queue
  // if bootstrap hasn't completed yet, or immediately if it has.
  // Trigger initialization now (will queue if bootstrap not ready, or execute immediately)
  forbiddenValidator.initialize();
})(window);
